/**
 * SCADA screens — plant-internal asset management, one panel per group of
 * slides in the operator's monthly performance deck.
 *
 *   performance   deck slides 1, 2, 6   contractual KPIs, daily table, exclusions
 *   production    deck slides 3, 4      energy + irradiation, daily and hourly
 *   sensors       deck slides 7-10      MET station cross-validation
 *   availability  deck slide 11         EPME / EPLE by asset class
 *   inverters     deck slides 15, 16    per-inverter fleet ranking
 *   strings       deck slides 17-19     combiner-box deviation ranking
 *
 * Only mounted when useScadaCapability() reports a client_* dataset for the
 * organisation. Every panel labels whether a number is the operator's REPORTED
 * figure or our own COMPUTED one, because the two are not interchangeable and
 * the whole point of holding the raw SCADA is being able to tell them apart.
 *
 * Where a deck panel is not reproducible from the available tags, the panel says
 * so inline rather than rendering a plausible-looking substitute.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, Stack, Skeleton, Alert, AlertTitle, Chip, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, ToggleButton,
  ToggleButtonGroup, IconButton, TableContainer,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import { PieChart } from '@mui/x-charts/PieChart';
// Composition primitives, for the one chart that has to mix bars and lines on a
// single shared axis — see "Daily average specific production" below.
import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { BarPlot } from '@mui/x-charts/BarChart';
import { LinePlot, MarkPlot } from '@mui/x-charts/LineChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight';
import axios from 'axios';
import dayjs from 'dayjs';

import KpiCard from './KpiCard';
import TechnicalWorkspaceTabs from './TechnicalWorkspaceTabs';
import StandardPeriodPicker from './PeriodPicker';
import useScadaCapability from './useScadaCapability';
import { useThemeColors } from '../theme/ThemeModeContext';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const AMBER = '#D98A2B';
const GOOD = '#22c55e';
const WARN = '#f59e0b';
const BAD = '#ef4444';
const MUTED = '#94a3b8';

const fmtPct = (v, d = 2) => (v == null ? '—' : `${(v * 100).toFixed(d)}%`);
const fmtNum = (v, d = 2) => (v == null ? '—' : Number(v).toLocaleString('en-AU',
  { minimumFractionDigits: d, maximumFractionDigits: d }));
const SUB_DAILY = new Set(['5min', '30min', 'hourly']);

/** Band-scale key. MUST be unique per bucket: duplicate keys make MUI dedupe the
 *  domain, which silently collapsed 336 half-hour buckets onto a single 24-hour
 *  axis and drew one bell curve instead of seven days. */
const bucketKey = (row) => String(row.bucket || row.day || '');

/** What a tick actually displays — short, since keys carry the full timestamp. */
const tickLabel = (key, freq) => (SUB_DAILY.has(freq)
  ? `${key.slice(5, 10)} ${key.slice(11, 16)}`   // MM-DD HH:MM
  : key.slice(5, 10));                            // MM-DD

/** Show at most `max` tick labels regardless of bucket count. */
const everyNth = (n, max = 12) => {
  const step = Math.max(1, Math.ceil(n / max));
  return (_v, i) => i % step === 0;
};

const fmtInt = (v) => (v == null ? '—' : Number(v).toLocaleString('en-AU',
  { maximumFractionDigits: 0 }));

// Deviation colour ramp, shared by the sensor and combiner-box panels so the
// same magnitude always reads the same way.
function devColour(pct, { warn = 2, bad = 5 } = {}) {
  const a = Math.abs(pct ?? 0);
  if (a >= bad) return BAD;
  if (a >= warn) return WARN;
  return GOOD;
}

// ── Chart primitives ────────────────────────────────────────────────────────
// Diverging encoding for "how far from the reference", used wherever the reader's
// job is polarity rather than magnitude. Two hues that read as opposite (warm
// below, cool above) with a neutral gray midpoint — a hue at the midpoint would
// stop it reading as "nothing". Validated for CVD: worst adjacent ΔE 26.7
// (protan) / 35.2 (normal vision), passing in both light and dark surfaces.
const DIV_BELOW = BAD;        // warm — under the reference
const DIV_ABOVE = '#3b82f6';  // cool — over the reference

// `dead` is a deadband, as a fraction of span, inside which a value reads as
// neutral. It is not cosmetic: without it, ordinary unit-to-unit scatter of a few
// percent colours up and the grid becomes a wash in which the one unit that
// matters no longer dominates. Intensity ramps from the edge of the deadband, so
// the visible range spans real deviation rather than noise.
function divergingFill(pct, span, dead = 0.3) {
  if (pct == null || Number.isNaN(pct)) return { bg: 'transparent', op: 0.06 };
  const t = Math.min(1, Math.abs(pct) / span);
  if (t <= dead) return { bg: MUTED, op: 0.16 };
  const k = (t - dead) / (1 - dead);
  return { bg: pct < 0 ? DIV_BELOW : DIV_ABOVE, op: 0.25 + 0.75 * k };
}

/** Scale legend for the diverging ramp. Identity is never colour-alone. */
function DivergingLegend({ span, unit = '%', belowLabel = 'below', aboveLabel = 'above' }) {
  const steps = [-1, -0.6, -0.25, 0, 0.25, 0.6, 1];
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
        {`${belowLabel} −${span}${unit}`}
      </Typography>
      {steps.map((s) => {
        const f = divergingFill(s * span, span);
        return (
          <Box key={s} sx={{
            width: 16, height: 9, borderRadius: '2px',
            bgcolor: f.bg, opacity: f.op,
          }} />
        );
      })}
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
        {`+${span}${unit} ${aboveLabel}`}
      </Typography>
    </Stack>
  );
}

/**
 * Emphasis bars: the outliers carry the accent, everything else is de-emphasis
 * gray. The deck's own equivalent is 75 identical bars, which buries the one
 * inverter that matters — emphasis is the honest form when a single item is the
 * story. Every bar is direct-labelled, which also discharges the sub-3:1
 * contrast obligation on the accent hue.
 */
/** `signed` is for deviations, where the sign is the point. Off for plain
 *  magnitudes like hours — "+186.9 h" reads as a change rather than a total. */
function EmphasisBars({ rows, threshold, unit = '%', height = 15, signed = true }) {
  const span = Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  return (
    <Stack spacing={0.75}>
      {rows.map((r) => {
        // A row may set `flagged` itself. That matters when every row already
        // exceeds the threshold: colouring all of them identically re-encodes bar
        // length and says nothing, whereas an explicit flag can carry a genuinely
        // orthogonal fact — e.g. "this box shares an inverter with four others".
        const flagged = r.flagged ?? (Math.abs(r.value) >= threshold);
        return (
          <Stack key={r.label} direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" sx={{
              width: 84, flexShrink: 0, fontSize: 11, textAlign: 'right',
              fontWeight: flagged ? 600 : 400,
              color: flagged ? 'text.primary' : 'text.secondary',
            }}>
              {r.label}
            </Typography>
            <Box sx={{ flex: 1, position: 'relative', height }}>
              <Box sx={{
                position: 'absolute', left: 0, top: 0, height,
                width: `${(Math.abs(r.value) / span) * 100}%`,
                bgcolor: flagged ? BAD : MUTED,
                opacity: flagged ? 0.9 : 0.35,
                borderRadius: '0 4px 4px 0',
              }} />
            </Box>
            <Typography variant="caption" sx={{
              width: 62, flexShrink: 0, fontSize: 11,
              fontWeight: flagged ? 600 : 400,
              color: flagged ? BAD : 'text.secondary',
            }}>
              {`${signed && r.value > 0 ? '+' : ''}${fmtNum(r.value, 1)}${unit}`}
            </Typography>
            {r.sub && (
              <Typography variant="caption" sx={{
                fontSize: 10, fontFamily: 'monospace',
                color: r.badge ? BAD : 'text.secondary',
                fontWeight: r.badge ? 600 : 400,
              }}>
                {r.sub}
              </Typography>
            )}
            {/* Secondary channel. When every bar is already extreme, magnitude
                belongs to bar length and a second fact needs its own mark rather
                than stealing the fill colour. */}
            {r.badge && (
              <Chip size="small" label={r.badge} sx={{
                height: 16, fontSize: 9, borderRadius: '3px',
                bgcolor: `${BAD}22`, color: BAD, fontWeight: 600,
              }} />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

/**
 * Distribution of deviations. Shows the fleet's SHAPE — whether the tail is one
 * isolated outlier or a gradual slope — which a ranked list cannot convey. Bins
 * carry the same diverging encoding as the grids so the colours mean one thing
 * across the whole screen.
 */
function DeviationHistogram({ values, span, binCount = 21, height = 96 }) {
  const { bins, maxN, edges } = useMemo(() => {
    const lo = -span;
    const hi = span;
    const w = (hi - lo) / binCount;
    const b = new Array(binCount).fill(0);
    values.forEach((v) => {
      if (v == null) return;
      const i = Math.min(binCount - 1, Math.max(0, Math.floor((v - lo) / w)));
      b[i] += 1;
    });
    return { bins: b, maxN: Math.max(...b, 1), edges: { lo, w } };
  }, [values, span, binCount]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height }}>
        {bins.map((n, i) => {
          const centre = edges.lo + edges.w * (i + 0.5);
          const f = divergingFill(centre, span);
          return (
            <Tooltip key={i} title={`${fmtNum(edges.lo + edges.w * i, 0)}% to ${fmtNum(edges.lo + edges.w * (i + 1), 0)}% — ${n} unit${n === 1 ? '' : 's'}`}>
              <Box sx={{
                flex: 1, height: `${Math.max(n ? 6 : 0, (n / maxN) * 100)}%`,
                bgcolor: f.bg, opacity: n ? Math.max(0.35, f.op) : 0,
                borderRadius: '2px 2px 0 0',
              }} />
            </Tooltip>
          );
        })}
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        {[`−${span}%`, 'fleet mean', `+${span}%`].map((t) => (
          <Typography key={t} variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            {t}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

/**
 * DC collection grid — every combiner box in the plant: inverters across, DC
 * input position down.
 *
 * This is the diagnostic the operator's ranked table cannot give: a vertical dark
 * stripe means the whole inverter is down (one fault), whereas an isolated dark
 * cell means a single box or its strings (a different fault, a different truck
 * roll). Rows are ORDINAL positions, not SCADA SCB numbers — those are
 * non-contiguous (2,3,7,8,10,11) and vary per inverter.
 */
function CollectionGrid({ boxes, span }) {
  const { inverters, cells, maxPos } = useMemo(() => {
    const byInv = new Map();
    boxes.forEach((b) => {
      const inv = b.inverter || '';
      if (!byInv.has(inv)) byInv.set(inv, []);
      byInv.get(inv).push(b);
    });
    const invList = [...byInv.keys()].sort();
    const map = new Map();
    let mx = 0;
    invList.forEach((inv) => {
      // Ordinal position within the inverter, ordered by the SCADA SCB number.
      const sorted = byInv.get(inv).slice().sort((a, b) => {
        const na = Number((/scb_(\d+)/.exec(a.parent) || [])[1] || 0);
        const nb = Number((/scb_(\d+)/.exec(b.parent) || [])[1] || 0);
        return na - nb;
      });
      sorted.forEach((b, i) => { map.set(`${inv}:${i}`, b); });
      if (sorted.length > mx) mx = sorted.length;
    });
    return { inverters: invList, cells: map, maxPos: mx };
  }, [boxes]);

  if (!inverters.length) return null;
  const label = (inv) => inv.replace(/^.*pcu_(\d+)\.inv_(\d+)$/, '$1.$2');

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: `30px repeat(${inverters.length}, 15px)`,
          gap: '2px', minWidth: 'max-content',
        }}>
          <Box sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper' }} />
          {inverters.map((inv) => (
            <Typography key={inv} variant="caption" sx={{
              fontSize: 8, color: 'text.secondary', height: 34,
              writingMode: 'vertical-rl', textOrientation: 'mixed',
              justifySelf: 'center', lineHeight: 1,
            }}>
              {label(inv)}
            </Typography>
          ))}
          {Array.from({ length: maxPos }, (_, pos) => (
            <React.Fragment key={pos}>
              <Typography variant="caption" sx={{
                position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper',
                fontSize: 9, color: 'text.secondary', textAlign: 'right', pr: 0.5,
                lineHeight: '17px',
              }}>
                {pos + 1}
              </Typography>
              {inverters.map((inv) => {
                const b = cells.get(`${inv}:${pos}`);
                const f = divergingFill(b ? b.deviation_pct : null, span);
                return (
                  <Tooltip key={inv} title={b
                    ? `${b.display_name} — ${fmtNum(b.ah_per_string, 1)} Ah/string (${b.deviation_pct > 0 ? '+' : ''}${fmtNum(b.deviation_pct, 1)}% vs fleet) · rank ${b.rank}`
                    : `${label(inv)} position ${pos + 1} — not present`}>
                    <Box sx={{
                      height: 17, borderRadius: '2px',
                      bgcolor: f.bg, opacity: f.op,
                      border: b ? 'none' : '1px dashed', borderColor: 'divider',
                    }} />
                  </Tooltip>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary"
                  sx={{ fontSize: 10, mt: 0.5, display: 'block' }}>
        columns = inverter (PCU.position) · rows = DC input, ordinal · a vertical
        stripe is one inverter-level fault; an isolated cell is one box
      </Typography>
      <DivergingLegend span={span} />
    </Box>
  );
}

function PlantGrid({ rows, span, unit, valueLabel }) {
  const { pcus, cells } = useMemo(() => {
    const map = new Map();
    const pcuSet = new Set();
    rows.forEach((r) => {
      const m = /pcu_(\d+)\.inv_(\d+)/.exec(r.parent || '');
      if (!m) return;
      const pcu = Number(m[1]);
      const inv = Number(m[2]);
      pcuSet.add(pcu);
      map.set(`${pcu}:${inv}`, r);
    });
    return { pcus: [...pcuSet].sort((a, b) => a - b), cells: map };
  }, [rows]);

  if (!pcus.length) return null;

  return (
    <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: `34px repeat(${pcus.length}, minmax(26px, 1fr))`,
        gap: '2px', minWidth: pcus.length * 28 + 34,
      }}>
        <Box />
        {pcus.map((p) => (
          <Typography key={p} variant="caption" sx={{
            fontSize: 9, textAlign: 'center', color: 'text.secondary' }}>
            {String(p).padStart(2, '0')}
          </Typography>
        ))}
        {[1, 2, 3, 4].map((inv) => (
          <React.Fragment key={inv}>
            <Typography variant="caption" sx={{
              fontSize: 10, color: 'text.secondary', textAlign: 'right', pr: 0.75,
              lineHeight: '22px' }}>
              {`.${inv}`}
            </Typography>
            {pcus.map((p) => {
              const r = cells.get(`${p}:${inv}`);
              const f = divergingFill(r ? r.dev : null, span);
              return (
                <Tooltip key={p} title={r
                  ? `${r.label} — ${fmtNum(r.value, 2)} ${unit} (${r.dev > 0 ? '+' : ''}${fmtNum(r.dev, 1)}% vs fleet)${r.sub ? ` · ${r.sub}` : ''}`
                  : `PCU ${p} position .${inv} — no data`}>
                  <Box sx={{
                    height: 22, borderRadius: '2px',
                    bgcolor: f.bg, opacity: f.op,
                    border: r ? 'none' : '1px dashed',
                    borderColor: 'divider',
                    cursor: r ? 'pointer' : 'default',
                  }} />
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary"
                  sx={{ fontSize: 10, mt: 0.5, display: 'block' }}>
        {`columns = PCU · rows = position within PCU · shade = ${valueLabel} vs fleet mean`}
      </Typography>
      <DivergingLegend span={span} />
    </Box>
  );
}


/**
 * Failure-mode scatter: yield against downtime.
 *
 * The distinction this makes is the one an engineer actually needs, and no chart
 * in the operator's deck offers it. Two inverters can be equally short of the
 * fleet mean for opposite reasons:
 *
 *   low yield + high downtime  → an availability fault. It was off. Fix the fault.
 *   low yield + ~zero downtime → it ran all month and still underperformed:
 *                                soiling, string mismatch, degradation, clipping.
 *
 * Same symptom, different truck roll. Split into two series so the legend carries
 * identity rather than colour alone.
 */
function FailureModeScatter({ rows, mean, height = 300 }) {
  const { flagged, fleet } = useMemo(() => {
    const f = [];
    const n = [];
    rows.forEach((r, i) => {
      const pt = {
        x: r.outage_hours ?? 0,
        y: r.specific_production ?? 0,
        id: r.parent || `r${i}`,
        label: r.display_name,
      };
      (mean && r.specific_production < mean * 0.95 ? f : n).push(pt);
    });
    return { flagged: f, fleet: n };
  }, [rows, mean]);

  return (
    <ScatterChart
      height={height}
      grid={{ horizontal: true }}
      // tickNumber caps the axis at a readable handful — the default steps every
      // 2 hours across an 80-hour range and the labels turn into a smear.
      xAxis={[{ label: 'daylight hours below 2 kW', tickNumber: 8,
                tickLabelStyle: { fontSize: 10 } }]}
      yAxis={[{ label: 'kWh/kWp', tickNumber: 6, tickLabelStyle: { fontSize: 10 } }]}
      series={[
        {
          label: 'Fleet', data: fleet, color: MUTED, markerSize: 4,
          valueFormatter: (v) => (v ? `${v.label}: ${fmtNum(v.y, 2)} kWh/kWp · ${fmtNum(v.x, 1)}h` : ''),
        },
        {
          label: 'Below 95% of mean', data: flagged, color: BAD, markerSize: 6,
          valueFormatter: (v) => (v ? `${v.label}: ${fmtNum(v.y, 2)} kWh/kWp · ${fmtNum(v.x, 1)}h` : ''),
        },
      ]}
      margin={{ top: 12, right: 16, bottom: 48, left: 60 }}
    >
      {mean && <ChartsReferenceLine y={mean}
                 lineStyle={{ stroke: MUTED, strokeWidth: 2, strokeOpacity: 0.6 }}
                 labelStyle={{ fontSize: 10, fill: MUTED }}
                 label={`fleet mean ${fmtNum(mean, 1)}`} labelAlign="end" />}
    </ScatterChart>
  );
}


// ── Chrome ──────────────────────────────────────────────────────────────────

function PanelHeader({ title, subtitle, cap, onRefresh, period, onPeriodChange, presets,
                      showFrequency = false, granularity = null, afterTitle = null }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{title}</Typography>
        {/* Sits hard against the title, before the spacer: a control that chooses
            WHICH thing the panel is showing belongs beside that panel's name, not
            adrift on a line of its own below it. */}
        {afterTitle}
        <Box sx={{ flex: 1 }} />
        {/* granularity is the floor the panel's data can actually support, so
            finer buckets render disabled rather than silently doing nothing:
            reported figures exist only daily, raw SCADA goes down to 5-minutely. */}
        {period && onPeriodChange && (
          <StandardPeriodPicker value={period} onChange={onPeriodChange}
                                presets={presets} showFrequency={showFrequency}
                                granularity={granularity} size="small" />
        )}
        {onRefresh && (
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={onRefresh}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
        {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        {cap?.siteName && <Chip size="small" label={cap.siteName} />}
        {/* The DUID makes it unambiguous which registry asset this data is about,
            and it is the key the market-data pages are scoped by. */}
        {cap?.siteAssetId && (
          <Tooltip title="Registry asset (DUID) this plant data maps to">
            <Chip size="small" color="primary" variant="outlined" label={cap.siteAssetId}
                  sx={{ fontFamily: 'monospace', fontSize: 11 }} />
          </Tooltip>
        )}
        {/* The window actually queried, not the window available — so a chart and
            its label can never disagree about what is on screen. */}
        {(period?.start || cap?.periodStart) && (
          <Chip size="small" variant="outlined" label={
            `${isoDate(period?.start) || cap.periodStart} → ${isoDate(period?.end) || cap.periodEnd}`
          } />
        )}
      </Stack>
    </Box>
  );
}

/** Colour is keyed to the FILTER, never to its position in the series list.
 *  Only filters with hours in the window are drawn, so the legend shrinks on a
 *  clean period — and a fixed key→colour map is what stops that shrinking from
 *  repainting the survivors. Order matches the operator's own legend. */
const EXCLUSION_SERIES = [
  { key: 'poa_erroneous',       label: 'POA err.',            color: '#60a5fa' },
  { key: 'ambient_erroneous',   label: 'Ambient err.',        color: '#1e40af' },
  { key: 'power_erroneous',     label: 'Power err.',          color: '#7c3aed' },
  { key: 'ghi_erroneous',       label: 'GHI err.',            color: '#f97316' },
  { key: 'pf_below_threshold',  label: 'PF < 0.93',           color: '#0f766e' },
  { key: 'curtailment_ramping', label: 'Curtailment/ramping', color: '#ec4899' },
  { key: 'dispatch_flag_up',    label: 'Dispatch flag',       color: '#a78bfa' },
  { key: 'ghi_below_100',       label: 'GHI < 100 W/m²',      color: '#ef4444' },
  { key: 'wind_flood_stow',     label: 'Wind/flood stow',     color: '#eab308' },
  { key: 'force_majeure',       label: 'Force majeure',       color: '#78716c' },
  { key: 'site_event',          label: 'Site event',          color: '#f9a8d4' },
];

/** The nine reasons the operator's own "Hours Excluded" chart carries, in their order.
 *  Power Erroneous and Force Majeure are computed but absent from their legend, so they
 *  are left off this chart to keep it comparable band for band. */
const PCL_EXCLUSION_ORDER = [
  'poa_erroneous', 'ambient_erroneous', 'ghi_erroneous', 'pf_below_threshold',
  'curtailment_ramping', 'dispatch_flag_up', 'wind_flood_stow', 'ghi_below_100',
  'site_event',
];


/** The canonical colour for an exclusion filter. Curtailment and the dispatch flag
 *  appear both in the Performance exclusion mix and as the two lines on the inverter
 *  chart; looking them up here rather than restating a hex keeps one filter one
 *  colour across the app. Reusing an arbitrary hue would have been worse than
 *  inconsistent: violet already means "dispatch flag", so painting "curtailed" with
 *  it made a single colour carry two meanings on adjacent screens. */
const exColour = (key) => EXCLUSION_SERIES.find((x) => x.key === key)?.color;



/** Composition of exclusion hours over the whole period, as one stacked bar.
 *
 *  The denominator is the SUM of the reasons, which double-counts — the reasons
 *  overlap, so an hour can appear in three of them. That is what makes this a
 *  composition of *reasons* rather than of time: it answers "what drives the
 *  exclusions", not "how much of the month was excluded". Hours are shown beside
 *  each share so the double-counting is visible rather than implied.
 *
 *  Stacks column-reverse so the first series sits at the bottom, matching the
 *  bar chart beside it — the two must read in the same order to share a legend.
 */
function ExclusionComposition({ series }) {
  const rows = series?.rows || [];
  const parts = EXCLUSION_SERIES
    .map((s) => ({ ...s, hours: rows.reduce((a, r) => a + (r.excluded?.[s.key] || 0), 0) }))
    .filter((s) => s.hours > 0);
  const sum = parts.reduce((a, s) => a + s.hours, 0);
  if (!sum) return null;

  // Its own legend rather than borrowing the bar chart's below: identity must not
  // rest on colour alone, and a reader should not have to look down the card to
  // find out which slice is which.
  return (
    <Stack spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <PieChart
        width={132} height={132}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        hideLegend
        series={[{
          data: parts.map((s) => ({ id: s.key, value: s.hours,
                                    label: s.label, color: s.color })),
          innerRadius: 36, outerRadius: 62, paddingAngle: 1.5, cornerRadius: 2,
          highlightScope: { faded: 'global', highlighted: 'item' },
          faded: { innerRadius: 36, additionalRadius: -2, color: 'gray' },
          valueFormatter: (item) =>
            `${fmtNum(item?.value ?? 0, 2)} h · ${fmtPct((item?.value ?? 0) / sum, 1)}`,
        }]}
      />
      <Stack spacing={0.35} sx={{ minWidth: 0, width: '100%' }}>
        {parts.map((s) => (
          <Stack key={s.key} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: s.color,
                       flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {`${s.label} — ${fmtPct(s.hours / sum, 1)} (${fmtNum(s.hours, 0)} h)`}
            </Typography>
          </Stack>
        ))}
        <Typography sx={{ fontSize: 10, color: 'text.disabled', pt: 0.25 }}>
          {`${fmtNum(sum, 0)} h total`}
        </Typography>
      </Stack>
    </Stack>
  );
}


/** The exclusion mix sits with the period cards, not on the time-series chart: it
 *  is a summary of the whole window like they are, and it should be there in the
 *  table view too rather than only when charts happen to be showing. */
function ExclusionMixCard({ series, loading }) {
  if (loading) return <Skeleton variant="rounded" height="100%" />;
  if (!series?.rows?.length) return null;
  return (
    <SectionCard title="Exclusion mix" sx={{ height: '100%', minWidth: 0 }}
                 caption="Each filter's share of all exclusion hours in the period. Reasons overlap — an hour below 100 W/m² is usually also below the power-factor floor — so the total is a sum of reasons rather than the hours actually excluded.">
      <ExclusionComposition series={series} />
    </SectionCard>
  );
}


/** Chart view of the results series — the same numbers as the table beside it. */
function PerformanceCharts({ series, hourly, loading, error }) {
  const rows = series?.rows || [];
  const cells = hourly?.cells || [];
  const labels = rows.map((r) => r.bucket);
  const ticks = { valueFormatter: (v, ctx) => (ctx?.location === 'tick' ? v.slice(5) : v),
                  tickLabelInterval: everyNth(rows.length),
                  tickLabelStyle: { fontSize: 9 } };

  // ALL NINE REASONS, always, in the operator's own order — not just the ones that
  // fired. A reason at zero is a result: it says the plant was not wind-stowed and had
  // no site event, which is different from that reason being absent from the analysis.
  // Dropping empty bands also made the legend change shape between periods, so the
  // same colour could sit in a different position from one month to the next.
  const active = PCL_EXCLUSION_ORDER
    .map((k) => EXCLUSION_SERIES.find((s) => s.key === k))
    .filter(Boolean);

  if (error) return <Alert severity="warning">{error}</Alert>;
  if (loading) return <Stack spacing={2}><Skeleton height={240} /><Skeleton height={240} /></Stack>;
  if (!rows.length) {
    return <Typography variant="body2" color="text.secondary">No rows for this period.</Typography>;
  }

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <SectionCard title="Adjusted Measured Performance Index (%)" sx={{ minWidth: 0 }}
                   caption="Qualified energy ÷ availability-adjusted PVsyst energy, for each hour. An hour with no qualified samples is blank rather than zero — zero would read as total failure instead of nothing measured. The colour ramp is clamped to 80–110% so the band everyone reads is legible; hours outside that saturate and keep their real figure on hover.">
        <HourGrid cells={cells} field="mpi_adjusted" unit="%" colour={AMBER}
                  domain={[80, 110]} digits={1} />
      </SectionCard>

      <SectionCard title="Hours qualified" sx={{ minWidth: 0 }}
                   caption="Fraction of each hour whose 5-minute samples qualified, 0 to 1. Partly-qualified hours are normal: every filter except the GHI floor works sample by sample.">
        <HourGrid cells={cells} field="hours_included" unit="h" colour={GOOD} />
      </SectionCard>

      <SectionCard title="Hours excluded by reason"
                   caption="Exclusion reasons OVERLAP — an hour below 100 W/m² is usually also below the power-factor floor — so the stack sums to more than the hours in the bucket. Read each band against itself over time, not the stack height as a total.">
        <BarChart height={300}
          xAxis={[{ scaleType: 'band', data: labels, ...ticks }]}
          series={active.map((s) => ({
            data: rows.map((r) => r.excluded?.[s.key] ?? 0),
            label: s.label, color: s.color, stack: 'excl',
          }))}
          margin={{ top: 12, right: 12, bottom: 28, left: 46 }}
          slotProps={{ legend: { labelStyle: { fontSize: 11 } } }}
        />
      </SectionCard>
    </Stack>
  );
}


/** `caption` is an explanation, not a label, so it hides behind an info icon
 *  rather than sitting beside the title — the same affordance the KPI tiles use.
 *  Inline it competed with the chart for attention on every card at once. */
function SectionCard({ title, caption, source, children, sx }) {
  return (
    <Card variant="outlined" sx={{ p: 2, ...sx }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
        {caption && (
          <Tooltip title={caption} arrow placement="top-start">
            <InfoOutlinedIcon sx={{
              fontSize: 14, color: 'text.disabled', cursor: 'help',
              '&:hover': { color: 'text.secondary' },
            }} />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {source && (
          <Chip size="small" label={source}
                color={source === 'computed' ? 'primary' : 'default'}
                variant={source === 'computed' ? 'filled' : 'outlined'}
                sx={{ height: 20, fontSize: 10, textTransform: 'uppercase' }} />
        )}
      </Stack>
      {children}
    </Card>
  );
}

/** A deck panel we deliberately do not render, with the reason. Better than a
 *  plausible-looking chart built on an assumption the data cannot support. */
function NotAvailable({ title, reason }) {
  return (
    <Card variant="outlined" sx={{ p: 2, borderStyle: 'dashed' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <InfoOutlinedIcon fontSize="small" sx={{ color: MUTED }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {title}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">{reason}</Typography>
    </Card>
  );
}


// ── Period ──────────────────────────────────────────────────────────────────

const SCADA_PERIOD_KEY = 'veraxis.scada.period.v1';

/**
 * Calendar date from a picker value. The SCADA endpoints take calendar dates, not
 * instants.
 *
 * Must format through dayjs rather than slicing the ISO string: the picker stores
 * UTC instants, so local midnight on 1 April is "2026-03-31T…Z" for any viewer
 * east of UTC, and slicing that yields 31 March — a day of data silently added at
 * the front and a spurious "subset" marker on a window that is in fact complete.
 */
const isoDate = (v) => (v ? dayjs(v).format('YYYY-MM-DD') : null);

/**
 * Period state for the SCADA screens.
 *
 * Presets are anchored to the LOADED DATA, not to today. O&M data arrives a
 * month or more in arrears, so the shared "last 30 days" preset would resolve to
 * a window with nothing in it — the picker would look broken while being
 * technically correct. So "All" spans the ingested range and the relative chips
 * count back from the last loaded day.
 *
 * A persisted selection that no longer overlaps the loaded range is discarded
 * rather than honoured: a stale sessionStorage entry should not present an empty
 * screen as if there were no data.
 */
function useScadaPeriod(cap) {
  const { periodStart, periodEnd } = cap;

  const presets = useMemo(() => {
    if (!periodStart || !periodEnd) return [];
    const s = dayjs(periodStart);
    const e = dayjs(periodEnd);
    // The last whole calendar month covered by the data.
    const lastMonth = e.startOf('month');
    // Clamped by hand rather than via dayjs.max — that needs the minMax plugin,
    // which is not registered in this app.
    const back = (n) => {
      const from = e.subtract(n - 1, 'day');
      return { start: from.isBefore(s) ? s : from, end: e.endOf('day') };
    };
    return [
      { value: 'month', label: 'Month',
        range: () => ({ start: lastMonth.isBefore(s) ? s : lastMonth,
                        end: e.endOf('day') }) },
      { value: 'all', label: 'All',  range: () => ({ start: s, end: e.endOf('day') }) },
      { value: '30d', label: '30D',  range: () => back(30) },
      { value: '7d',  label: '7D',   range: () => back(7) },
    ];
  }, [periodStart, periodEnd]);

  const [period, setPeriodState] = useState(null);

  useEffect(() => {
    if (!periodStart || !periodEnd || period) return;
    const lastMonth = dayjs(periodEnd).startOf('month');
    const full = {
      code: 'month',
      start: (lastMonth.isBefore(dayjs(periodStart))
        ? dayjs(periodStart) : lastMonth).startOf('day').toISOString(),
      end: dayjs(periodEnd).endOf('day').toISOString(),
      frequency: 'daily',
      label: 'Month',
    };
    let next = full;
    try {
      const raw = sessionStorage.getItem(SCADA_PERIOD_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        // Keep it only if it actually overlaps what is loaded.
        if (p?.start && p?.end
            && isoDate(p.end) >= isoDate(periodStart)
            && isoDate(p.start) <= isoDate(periodEnd)) {
          next = p;
        }
      }
    } catch { /* unavailable or corrupt storage — fall back to the full range */ }
    setPeriodState(next);
  }, [periodStart, periodEnd, period]);

  const setPeriod = useCallback((p) => {
    setPeriodState(p);
    try { sessionStorage.setItem(SCADA_PERIOD_KEY, JSON.stringify(p)); }
    catch { /* quota / private mode — state still updates */ }
  }, []);

  return { period, setPeriod, presets };
}


// ── Data hook ───────────────────────────────────────────────────────────────

function useScadaPanel(path, cap, extraParams, period) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  // The selected window, falling back to the full loaded ingest period. Never
  // "last 30 days" from today — O&M data arrives a month in arrears, so that
  // window is always empty.
  const from = isoDate(period?.start) || cap?.periodStart;
  const to = isoDate(period?.end) || cap?.periodEnd;
  // Pass the dataset capability resolved for this portfolio. Without it the
  // backend refuses to guess when the org holds more than one client dataset —
  // and it re-validates the name against the allowlist regardless.
  const dataset = cap?.dataset;
  const frequency = period?.frequency;
  const extraKey = JSON.stringify(extraParams || {});

  useEffect(() => {
    if (!cap?.available || !from || !to) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API_BASE_URL}/api/scada/${path}`,
              { params: { from, to, ...(dataset ? { dataset } : {}),
                           ...(frequency ? { frequency } : {}), ...(extraParams || {}) } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.error || 'Request failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, cap?.available, from, to, dataset, frequency, extraKey, nonce]);

  return { data, loading, error, refresh: () => setNonce((n) => n + 1) };
}


// ── performance — deck slides 1, 2, 6 ───────────────────────────────────────

function PerformancePanel({ cap, period, setPeriod, presets }) {
  const { data, loading, error, refresh } = useScadaPanel('performance', cap, null, period);
  // Chart by default: the shape over time is the first question, and the table is
  // there for the row that needs checking against the operator's own.
  const [view, setView] = useState('chart');
  const t = data?.totals || {};
  const pending = t.pending || {};
  const coverage = t.coverage || {};
  const req = t.requested;

  // A card whose underlying tags span less than the selected window is marked, so
  // "All" can never show three months of energy beside one month of soiling as
  // though both covered the same period.
  const narrower = (key) => {
    const c = coverage[key];
    if (!c?.first_day || !req) return null;
    return (c.first_day > req.from || c.last_day < req.to)
      ? `${c.first_day} → ${c.last_day}` : null;
  };

  // All eight figures the operator publishes, in their order. Every one is now
  // derived from telemetry; a card that ever loses its inputs falls back to a
  // placeholder carrying the reason, and is never filled from the operator's
  // own report.
  const TILES = [
    { key: 'measured_energy',           label: 'Meas. Energy (MWh)',               fmt: (v) => fmtNum(v, 2) },
    { key: 'mpi_adjusted',              label: 'Adjusted MPI (%)',                 fmt: (v) => fmtPct(v) },
    { key: 'hours_qualified',           label: '# Qualified Hours',                fmt: (v) => fmtNum(v, 2) },
    { key: 'qualified_energy',          label: 'Qualified Energy (MWh)',           fmt: (v) => fmtNum(v, 2) },
    { key: 'avail_adj_expected_energy', label: 'Avail Adj. Expected Energy (MWh)', fmt: (v) => fmtNum(v, 2) },
    { key: 'soiling_ratio',             label: 'Soiling',                          fmt: (v) => fmtNum(v, 2) },
    { key: 'albedo',                    label: 'Albedo',                           fmt: (v) => fmtNum(v, 2) },
    { key: 'om_availability',           label: 'O&M Availability (%)',             fmt: (v) => fmtPct(v, 1) },
  ];

  return (
    <>
      <PanelHeader title="Performance" cap={cap} onRefresh={refresh}
        period={period} onPeriodChange={setPeriod} presets={presets}
        showFrequency granularity="daily" />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* One row: the exclusion mix takes a fifth, the eight tiles share the rest.
          The mix is a period figure like the tiles are, so it belongs on their line
          rather than on one of its own. Four tile columns in that 80% puts them on
          two rows, which is also the height the donut needs. */}
      <Box sx={{
        display: 'grid', gap: 1.5, mb: 2, alignItems: 'stretch',
        gridTemplateColumns: { xs: '1fr', md: '20% 1fr' },
      }}>
        <ExclusionMixCard series={data?.series} loading={loading} />
        <Box sx={{
          display: 'grid', gap: 1.5, minWidth: 0,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        }}>
          {TILES.map((tile) => {
            const why = pending[tile.key];
            const v = t[tile.key];
            const short = why ? null : narrower(tile.key);
            return (
              <KpiCard key={tile.key} label={short ? `${tile.label} *` : tile.label}
                       loading={loading}
                       placeholder={!loading && Boolean(why)}
                       value={v == null ? '—' : tile.fmt(v)}
                       accent={short ? WARN : undefined}
                       tooltip={why
                         || (short && `These tags only cover ${short}, narrower than the selected window`)
                         || undefined} />
            );
          })}
        </Box>
      </Box>

      <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={view}
                           onChange={(_e, v) => v && setView(v)}>
          <ToggleButton value="chart" sx={{ px: 1.5, fontSize: 11 }}>Chart</ToggleButton>
          <ToggleButton value="table" sx={{ px: 1.5, fontSize: 11 }}>Table</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {view === 'chart'
        ? <PerformanceCharts series={data?.series} hourly={data?.hourly}
                             error={data?.series_error} loading={loading} />
        : <ResultsTable series={data?.series} error={data?.series_error}
                        loading={loading} />}
    </>
  );
}


/** The operator's results table, at whatever frequency the header selects.
 *
 * Numbers are right-aligned and tabular-figure so digits line up column-wise;
 * a mixed-alignment table of energies is unreadable at a glance. Two scopes are
 * mixed here on purpose, matching the operator's own layout: Measured Energy and
 * O&M Availability cover every hour in the bucket, while anything Qualified or
 * PVsyst covers only the qualified ones. */
function ResultsTable({ series, error, loading }) {
  const rows = series?.rows || [];
  const pct = (v, d = 1) => (v == null ? '—' : `${fmtNum(v * 100, d)}%`);
  // `width` is set only on the narrow columns; the energy columns divide whatever
  // is left, which is what keeps eleven columns inside the viewport.
  const COLS = [
    { key: 'bucket',                  label: 'Date',                                fmt: (v) => v, num: false, width: 86 },
    { key: 'measured_energy',         label: 'Measured Energy (MWh)',               fmt: (v) => fmtNum(v, 2) },
    { key: 'qualified_energy',        label: 'Qualified Energy (MWh)',              fmt: (v) => fmtNum(v, 2) },
    { key: 'om_availability',         label: 'O&M Availability (%)',                fmt: (v) => pct(v), width: 92 },
    { key: 'pvsyst_energy',           label: 'PVsyst Energy (MWh)',                 fmt: (v) => fmtNum(v, 2) },
    { key: 'avail_adj_pvsyst_energy', label: 'Avail. Adjusted PVsyst Energy (MWh)', fmt: (v) => fmtNum(v, 2) },
    { key: 'mpi',                     label: 'Measured Performance Index',          fmt: (v) => pct(v, 2), width: 104 },
    { key: 'hours_curtailed',         label: 'Hours Site Curtailed',                fmt: (v) => fmtNum(v, 2), width: 84 },
    { key: 'hours_included',          label: 'Hours Included',                      fmt: (v) => fmtNum(v, 2), width: 78 },
    // MEAN, not the operator's sum. Their column is hours x mean ratio, which is
    // not a ratio and grows with the bucket — a monthly row read 100.70 where a
    // daily row read 7.99, so the same physical condition changed by an order of
    // magnitude with the frequency control. The mean is the quantity these actually
    // are, and it is comparable across rows and against the card above.
    { key: 'soiling_mean',            label: 'Soiling',                             fmt: (v) => fmtNum(v, 3), width: 68,
      hint: 'Mean soiling ratio over the bucket’s qualified hours — dirty reference cell over clean. 1.00 is clean. The operator publishes hours x this ratio instead, which scales with bucket length.' },
    { key: 'albedo_mean',             label: 'Albedo',                              fmt: (v) => fmtNum(v, 3), width: 66,
      hint: 'Mean albedo over the bucket’s qualified hours — reflected over incident GHI. The operator publishes hours x this ratio instead.' },
  ];

  return (
    <SectionCard title="Performance results">
      {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
      {loading ? <Skeleton height={260} /> : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No rows for this period.
        </Typography>
      ) : (
        // Fixed layout so eleven columns share the width evenly rather than each
        // being sized by its longest cell. Headers wrap; the numbers never do.
        // overflowX stays as a fallback for genuinely narrow screens.
        <TableContainer sx={{ overflowX: 'auto', maxHeight: 560 }}>
          <Table size="small" stickyHeader sx={{
            width: '100%', tableLayout: 'fixed',
            '& th': {
              whiteSpace: 'normal', lineHeight: 1.2, fontSize: 11,
              px: 0.75, verticalAlign: 'bottom',
            },
            '& tbody td': {
              whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
              fontSize: 12, px: 0.75,
            },
          }}>
            <TableHead>
              <TableRow>
                {COLS.map((c) => (
                  <TableCell key={c.key} align={c.num === false ? 'left' : 'right'}
                             sx={c.width ? { width: c.width } : undefined}>
                    {c.hint ? (
                      <Tooltip title={c.hint} arrow placement="top">
                        <Box component="span" sx={{ borderBottom: '1px dotted', cursor: 'help' }}>
                          {c.label}
                        </Box>
                      </Tooltip>
                    ) : c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.bucket} hover>
                  {COLS.map((c) => (
                    <TableCell key={c.key} align={c.num === false ? 'left' : 'right'}>
                      {r[c.key] == null ? '—' : c.fmt(r[c.key])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}


// ── production — deck slides 3, 4 ───────────────────────────────────────────

/**
 * Day × hour heatmap, oriented as a time series: dates run along the x-axis and
 * hour-of-day down the y-axis, scrolling horizontally as the period grows.
 *
 * Hours are the fixed dimension (always 24) and dates are the open-ended one, so
 * hours belong on the axis that does not scroll. The hour column is sticky, which
 * keeps the y labels readable at any scroll offset.
 */
const HG_MIN = 20;       // px floor per date column; wider when there is room
const HG_LABEL = 42;     // px for the sticky hour gutter

/** `hours` clips the vertical range to [lo, hi]. Twelve rows of night on an energy
 *  grid are dead space, and they flatten the ramp for every daylight hour. */
function HourGrid({ cells, field, unit, colour, domain, digits = 2, hours }) {
  const { days, byHour, max } = useMemo(() => {
    const dayList = [...new Set((cells || []).map((c) => c.day))].sort();
    const idx = new Map(dayList.map((d, i) => [d, i]));
    // byHour[hour][dayIndex] — transposed relative to the source rows.
    const rows = Array.from({ length: 24 }, () => new Array(dayList.length).fill(null));
    let mx = 0;
    (cells || []).forEach((c) => {
      const i = idx.get(c.day);
      if (i === undefined || c.hour == null) return;
      rows[c.hour][i] = c[field];
      if (c[field] != null && c[field] > mx) mx = c[field];
    });
    return { days: dayList, byHour: rows, max: mx || 1 };
  }, [cells, field]);

  // A ramp from zero suits a quantity whose zero means "none" — energy, irradiance,
  // hours. It suits a ratio around 100% badly: every cell lands near full opacity
  // and the grid carries no information. Nor does spanning the observed range work
  // there, because a handful of outliers stretch it — full qualified hours in June
  // run from 28% to 226%, so a data-driven ramp compresses the 90-110 band everyone
  // actually reads. An explicit `domain` clamps it; values outside saturate at the
  // ends and keep their real figure in the tooltip.
  const base = domain ? domain[0] : 0;
  const span = ((domain ? domain[1] : max) - base) || 1;

  if (!days.length) return null;

  // Below this many dates the columns are wide enough for MM-DD to sit flat.
  const vertical = days.length > 20;

  const sticky = {
    position: 'sticky', left: 0, zIndex: 2,
    bgcolor: 'background.paper',
  };

  return (
    <Box sx={{ overflowX: 'auto', maxWidth: '100%', minWidth: 0, pb: 1 }}>
      {/* minmax(min, 1fr): date columns STRETCH to fill the card when the period
          is short, and stop shrinking at the minimum when it is long — at which
          point the grid outgrows the container and the parent's overflowX
          scrolls it. A fixed px width left the grid stuck at the left edge with
          dead space beside it. */}
      <Box sx={{
        display: 'grid', width: '100%',
        gridTemplateColumns: `${HG_LABEL}px repeat(${days.length}, minmax(${HG_MIN}px, 1fr))`,
        gap: '2px',
      }}>
        {/* Header: dates. Rotated only when the columns are too narrow for MM-DD
            to sit horizontally; the period can span months, so day-of-month alone
            would be ambiguous. */}
        <Box sx={{ ...sticky, gridRow: 1 }} />
        {days.map((d) => (
          <Typography key={d} variant="caption" sx={{
            fontSize: 9, color: 'text.secondary',
            height: vertical ? 44 : 18,
            ...(vertical
              ? { writingMode: 'vertical-rl', textOrientation: 'mixed' }
              : { alignSelf: 'end' }),
            justifySelf: 'center', lineHeight: 1,
          }}>
            {d.slice(5)}
          </Typography>
        ))}

        {byHour.map((row, h) => (
          (hours && (h < hours[0] || h > hours[1])) ? null : (
          <React.Fragment key={h}>
            <Typography variant="caption" sx={{
              ...sticky, fontSize: 10, color: 'text.secondary',
              textAlign: 'right', pr: 0.75, lineHeight: '15px',
            }}>
              {String(h).padStart(2, '0')}
            </Typography>
            {row.map((v, i) => (
              <Tooltip key={days[i]}
                       title={v == null ? '—'
                         : `${days[i]} ${String(h).padStart(2, '0')}:00 — ${fmtNum(v, digits)} ${unit}`}>
                <Box sx={{
                  height: 15, borderRadius: '2px',
                  bgcolor: v == null ? 'transparent' : colour,
                  opacity: v == null ? 0.08
                    : Math.max(0.12, Math.min(1, (v - base) / span)),
                  border: v == null ? '1px solid' : 'none',
                  borderColor: 'divider',
                }} />
              </Tooltip>
            ))}
          </React.Fragment>
          )
        ))}
      </Box>
    </Box>
  );
}

function ProductionPanel({ cap, period, setPeriod, presets }) {
  const { data, loading, error, refresh } = useScadaPanel('production', cap, null, period);
  const days = data?.daily?.days || [];
  const cells = data?.hourly?.cells || [];
  const freq = period?.frequency;
  const totalMwh = days.reduce((a, d) => a + (d.energy_mwh || 0), 0);
  const totalGhi = days.reduce((a, d) => a + (d.ghi_kwh_m2 || 0), 0);
  // PVsyst is hourly, so it is only offered at hourly or coarser — at 5-minute
  // grain the whole hour would land in one sub-bucket and the rest read zero.
  const hasExpected = Boolean(data?.daily?.has_expected);
  const totalExpected = hasExpected
    ? days.reduce((a, d) => a + (d.expected_mwh || 0), 0) : null;

  return (
    <>
      <PanelHeader title="Production & resource" cap={cap} onRefresh={refresh}
        period={period} onPeriodChange={setPeriod} presets={presets}
        showFrequency granularity="5min" />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* auto-fit + minmax stretches the tiles across the full width whatever
          their number — two cards take half each, four take a quarter — and wraps
          instead of overflowing once they hit the 160px floor. A flex row left
          them at natural width, bunched against the left edge. */}
      <Stack sx={{
        mb: 2, display: 'grid', gap: 1.5,
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      }}>
        <KpiCard label="Measured" loading={loading} value={`${fmtInt(totalMwh)} MWh`} />
        {hasExpected && (
          /* "all hours" is in the LABEL, not the tooltip. The operator publishes an
             Expected Energy of 4,158.63 MWh for June 2026 and this reads 13,441.50 —
             the same PVsyst run, but theirs is summed over qualified hours only while
             this spans all 720. Without the scope on the face of the card the two
             invite a comparison that makes our figure look three times too high. The
             qualified-hours figure, which IS comparable to theirs, is on the
             Performance screen as PVsyst Energy. */
          <KpiCard label="Expected (all hours)" loading={loading}
                   value={`${fmtInt(totalExpected)} MWh`}
                   tooltip="PVsyst E_Grid at the injection point, over every hour in the window — including hours the plant was curtailed or down, neither of which PVsyst models. That is why it sits well above measured: for June 2026, 13,441.50 against 11,493.03. The operator's Expected Energy column is the same run restricted to qualified hours, 4,158.63; ours on that basis is 4,099.61 and appears on the Performance screen." />
        )}
        {hasExpected && totalExpected > 0 && (
          <KpiCard label="Measured ÷ Expected (all hours)" loading={loading}
                   value={fmtPct(totalMwh / totalExpected, 1)}
                   tooltip="Not the performance index. MPI is measured over the qualified hours only, against the same hours' expected — this ratio spans every hour including curtailed and disturbed ones." />
        )}
        <KpiCard label="GHI" loading={loading} value={`${fmtNum(totalGhi, 1)} kWh/m²`} />
      </Stack>

      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <SectionCard title="Energy production"
                     caption={hasExpected
                       ? 'PPC active power against expected output, which is PVsyst E_Grid. That is a P50 under its own weather, not the weather that occurred — the gap is variance as much as performance, so lost energy comes from availability instead.'
                       : 'PPC active power, each sample weighted by its own feed cadence — April and May arrive at 5-minute, June at 1-minute'}>
          {loading ? <Skeleton height={240} /> : (
            <BarChart height={260}
              xAxis={[{ scaleType: 'band', data: days.map(bucketKey),
                        valueFormatter: (v, ctx) => (ctx?.location === 'tick'
                          ? tickLabel(v, freq) : v),
                        tickLabelInterval: everyNth(days.length),
                        tickLabelStyle: { fontSize: 9 } }]}
              series={[
                { data: days.map((d) => d.energy_mwh), label: 'Measured', color: AMBER },
                ...(hasExpected ? [{ data: days.map((d) => d.expected_mwh),
                                     label: 'Expected', color: MUTED }] : []),
                { data: days.map((d) => d.qualified_mwh), label: 'Qualified', color: GOOD },
              ]}
              margin={{ top: 12, right: 12, bottom: 28, left: 52 }}
            />
          )}
        </SectionCard>

        <SectionCard title="Irradiation"
                     caption="GHI and POA are the mean across the five MET stations; WMS PCU 1 reads about 4% high on POA in daylight and is kept in the mean, matching the operator. Qualified GHI is GHI integrated over the qualified samples only. Note it will not match the operator's own Qualified GHI column: theirs is a mean W/m² rather than an integral, and their June values imply means near 67 W/m² over hours their own filter requires to exceed 100, so it is not reproducible.">
          {loading ? <Skeleton height={240} /> : (
            <LineChart height={260}
              xAxis={[{ scaleType: 'point', data: days.map(bucketKey),
                        valueFormatter: (v, ctx) => (ctx?.location === 'tick'
                          ? tickLabel(v, freq) : v),
                        tickLabelInterval: everyNth(days.length),
                        tickLabelStyle: { fontSize: 9 } }]}
              series={[
                { data: days.map((d) => d.ghi_kwh_m2), label: 'GHI',
                  color: AMBER, showMark: false },
                { data: days.map((d) => d.poa_kwh_m2), label: 'POA',
                  color: MUTED, showMark: false },
                { data: days.map((d) => d.qualified_ghi_kwh_m2), label: 'Qualified GHI',
                  color: GOOD, showMark: false },
              ]}
              margin={{ top: 12, right: 12, bottom: 28, left: 52 }}
            />
          )}
        </SectionCard>

        <SectionCard title="Hourly energy" sx={{ minWidth: 0 }}>
          {loading ? <Skeleton height={420} /> : (
            <HourGrid cells={cells} field="energy_mwh" unit="MWh" colour={AMBER} />
          )}
        </SectionCard>

        <SectionCard title="Hourly GHI" sx={{ minWidth: 0 }}>
          {loading ? <Skeleton height={420} /> : (
            <HourGrid cells={cells} field="ghi_w_m2" unit="W/m²" colour="#eab308" />
          )}
        </SectionCard>
      </Stack>
    </>
  );
}


// ── sensors — deck slides 7-10 ──────────────────────────────────────────────

const SENSOR_GROUPS = [
  { key: 'soiling', label: 'Soiling' },
  { key: 'temperature', label: 'Temperature' },
  { key: 'irradiance', label: 'Irradiance' },
  { key: 'wind', label: 'Wind & humidity' },
];


/** Station x bucket grid — the same idea as HourGrid with stations for rows.
 *  Rows are few and fixed (five MET stations) while buckets grow with the period,
 *  so buckets run across and scroll, and the row labels stay in a sticky gutter. */
function StationGrid({ rows, valueKey, unit, colour, digits = 2, totals }) {
  const { stations, buckets, cell } = useMemo(() => {
    const st = [...new Set((rows || []).map((r) => r.display_name))].sort();
    const bk = [...new Set((rows || []).map((r) => r.bucket || r.day))].sort();
    const map = new Map((rows || []).map(
      (r) => [`${r.display_name}|${r.bucket || r.day}`, r[valueKey]]));
    return { stations: st, buckets: bk, cell: map };
  }, [rows, valueKey]);

  if (!stations.length) return null;
  const max = Math.max(...[...cell.values()].filter((v) => v != null), 0) || 1;
  const sticky = { position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper' };

  return (
    <Box sx={{ overflowX: 'auto', maxWidth: '100%', minWidth: 0, pb: 1 }}>
      <Box sx={{
        display: 'grid', width: '100%',
        gridTemplateColumns:
          `132px repeat(${buckets.length}, minmax(40px, 1fr))${totals ? ' 62px' : ''}`,
        gap: '2px',
      }}>
        <Box sx={{ ...sticky, gridRow: 1 }} />
        {buckets.map((b) => (
          <Typography key={b} variant="caption" sx={{
            fontSize: 10, color: 'text.secondary', justifySelf: 'center', lineHeight: 1,
            pb: 0.5,
          }}>
            {b.slice(8, 10) || b.slice(5)}
          </Typography>
        ))}
        {totals && (
          <Typography variant="caption" sx={{
            fontSize: 10, color: 'text.secondary', justifySelf: 'center', fontWeight: 600,
            pb: 0.5,
          }}>
            Total
          </Typography>
        )}

        {stations.map((st) => (
          <React.Fragment key={st}>
            <Typography variant="caption" sx={{
              ...sticky, fontSize: 11, color: 'text.secondary',
              textAlign: 'right', pr: 1, lineHeight: '30px', whiteSpace: 'nowrap',
            }}>
              {st}
            </Typography>
            {buckets.map((b) => {
              const v = cell.get(`${st}|${b}`);
              return (
                <Tooltip key={b} title={v == null ? '—'
                  : `${st} · ${b.slice(0, 10)} — ${fmtNum(v, digits)}${unit}`}>
                  <Box sx={{
                    height: 30, borderRadius: '3px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: v == null ? 'transparent' : colour,
                    opacity: v == null ? 0.08 : Math.max(0.12, Math.min(1, v / max)),
                    border: v == null ? '1px solid' : 'none', borderColor: 'divider',
                  }}>
                    {/* The number is printed as well as shaded: five rows leave room
                        for it, and the operator's own table shows the figures. */}
                    {v != null && (
                      <Typography sx={{ fontSize: 10, color: 'rgba(0,0,0,0.8)',
                                        lineHeight: 1 }}>
                        {fmtNum(v, digits)}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
            {totals && (
              <Typography sx={{ fontSize: 11, fontWeight: 600, textAlign: 'center',
                                lineHeight: '30px' }}>
                {totals[st] == null ? '—' : fmtNum(totals[st], digits)}
              </Typography>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

/** The soiling page, built to the same five figures the operator publishes:
 *  a headline loss, loss by station, the station x day grid with per-station
 *  totals, loss by day, and rainfall. */
function SoilingView({ soil, loading, freq }) {
  const rows = useMemo(() => soil?.by_station_day || [], [soil]);
  const stations = useMemo(() => soil?.by_station || [], [soil]);
  const byBucket = useMemo(() => soil?.by_bucket || [], [soil]);
  const rain = useMemo(() => soil?.precipitation || [], [soil]);
  const mismatched = stations.filter((s) => s.sensor_mismatch);

  const totals = useMemo(() => Object.fromEntries(
    stations.map((s) => [s.display_name, s.soiling_loss_pct])), [stations]);
  const bLabels = byBucket.map((b) => b.bucket || b.day);
  const rLabels = rain.map((r) => r.bucket || r.day);
  const ticks = (n) => ({
    valueFormatter: (v, ctx) => (ctx?.location === 'tick' ? tickLabel(v, freq) : v),
    tickLabelInterval: everyNth(n), tickLabelStyle: { fontSize: 9 },
  });

  if (loading) return <Skeleton height={520} />;
  if (!rows.length) {
    return (
      <SectionCard title="Soiling">
        <Typography variant="body2" color="text.secondary">
          No soiling data in this period — the reference cells appear in the
          1-minute export only.
        </Typography>
      </SectionCard>
    );
  }

  const dirtiest = stations.reduce(
    (a, x) => ((x.soiling_loss_pct ?? -Infinity) > (a?.soiling_loss_pct ?? -Infinity) ? x : a),
    null);
  const rainTotal = rain.reduce((a, r) => a + (r.rain_mm || 0), 0);

  return (
    <>
      {/* One row: the per-station chart takes 40%, the tiles the other 60%. Two
          tile columns inside that 60% puts them on two rows, which is about the
          height five horizontal bars want, so the row squares off. */}
      <Box sx={{
        display: 'grid', gap: 1.5, alignItems: 'stretch',
        gridTemplateColumns: { xs: '1fr', md: '40% 1fr' },
      }}>
        <SectionCard title="Soiling loss by station (%)"
                     sx={{ height: '100%', minWidth: 0 }}
                     caption={mismatched.length
                       ? `${soil?.basis} ${mismatched.map((x) => x.display_name).join(' and ')} `
                         + `read negative before the clamp (`
                         + `${mismatched.map((x) => fmtNum(x.soiling_loss_raw_pct, 2) + '%').join(', ')}`
                         + `), so their cell pair is miscalibrated and the figure shown is the floor, not clean glass.`
                       : soil?.basis}>
          <EmphasisBars threshold={1} rows={stations.map((s) => ({
            label: s.display_name, value: s.soiling_loss_pct ?? 0,
          }))} />
        </SectionCard>

        <Box sx={{
          display: 'grid', gap: 1.5, minWidth: 0,
          gridTemplateColumns: 'repeat(2, 1fr)',
        }}>
          <KpiCard label="Soiling loss" value={fmtNum(soil?.total_soiling_loss_pct, 2) + '%'}
                   tooltip={soil?.basis} />
          <KpiCard label="Dirtiest station"
                   value={dirtiest ? `${fmtNum(dirtiest.soiling_loss_pct, 2)}%` : '—'}
                   tooltip={dirtiest ? dirtiest.display_name : undefined}
                   accent={dirtiest && dirtiest.soiling_loss_pct > 1 ? WARN : undefined} />
          <KpiCard label="Rainfall" value={`${fmtNum(rainTotal, 1)} mm`}
                   tooltip={soil?.precipitation_note} />
          <KpiCard label="Stations" value={String(stations.length)} />
        </Box>
      </Box>

      <SectionCard title="Soiling loss by station & bucket (%)" sx={{ minWidth: 0 }}
                   caption="Stations down the left, buckets across — scroll sideways for the full period. Total is the station's mean over the window.">
        <StationGrid rows={rows} valueKey="soiling_loss_pct" unit="%"
                     colour="#eab308" digits={2} totals={totals} />
      </SectionCard>

      <SectionCard title="Soiling loss by bucket (%)"
                   caption="Mean across the five stations. A rainfall event should show as a step down here within a day or two.">
        <BarChart height={230}
          xAxis={[{ scaleType: 'band', data: bLabels, ...ticks(bLabels.length) }]}
          series={[{ data: byBucket.map((b) => b.soiling_loss_pct),
                     label: 'Soiling loss %', color: '#eab308' }]}
          margin={{ top: 12, right: 12, bottom: 28, left: 46 }}
        />
      </SectionCard>

      <SectionCard title="Precipitation (mm)" caption={soil?.precipitation_note}>
        <BarChart height={200}
          xAxis={[{ scaleType: 'band', data: rLabels, ...ticks(rLabels.length) }]}
          series={[{ data: rain.map((r) => r.rain_mm), label: 'mm',
                     color: '#3b82f6' }]}
          margin={{ top: 12, right: 12, bottom: 28, left: 46 }}
        />
      </SectionCard>
    </>
  );
}


/** Mean and maximum per station on one degree axis, with deviation carried by
 *  colour and on hover rather than as a plotted series or a column.
 *
 *  Deviation is not an independent measurement — it is the mean over the fleet mean —
 *  so encoding it as colour says the same thing the bar's position already says, just
 *  faster. As a second plotted series it would need a percentage axis beside a degree
 *  one, which is the chart construction that reliably misleads.
 *
 *  The hover also carries the median, because the mean and median part company at
 *  station 1 (15.68 against 16.70) in a way the bar cannot show: it reads low on cool
 *  samples specifically, not uniformly. */
/** Range-and-mean chart — one candle per bucket or station.
 *
 *  THREE MARKS ONLY: minimum, mean, maximum. The bar spans min to max and the bright
 *  rule is the mean. Quartiles were drawn here and taken out — they answered a
 *  question nobody was asking and made a five-column chart look like a statistics
 *  exercise. Whatever the bar cannot carry goes in the `footer` row underneath.
 *
 *  Not a financial candlestick: a candlestick body encodes OPEN and CLOSE, and "the
 *  temperature at midnight" is an artefact of where the bucket boundary fell, not a
 *  quantity anyone asks about.
 *
 *  Not zero-based, and deliberately so: these sit in a ~5-25 degree band and a
 *  zero-based axis would compress every range the chart exists to show. Safe here
 *  because nothing is encoded as a LENGTH from the axis — each mark is a position on
 *  a shared scale, which is exactly what a box plot is for. */
function RangeCandles({ rows, unit = '°C', digits = 2, colour = AMBER, height = 260,
                       allTicks = false, footer = null, footerLabel = null,
                       line = false, lineColour = GOOD }) {
  const usable = useMemo(
    () => (rows || []).filter((r) => r.min_v != null && r.max_v != null),
    [rows],
  );
  if (!usable.length) return null;

  const lo = Math.min(...usable.map((r) => r.min_v));
  const hi = Math.max(...usable.map((r) => r.max_v));
  const pad = ((hi - lo) || 1) * 0.08;
  const y0 = lo - pad;
  const y1 = hi + pad;
  const pct = (v) => ((y1 - v) / (y1 - y0)) * 100;
  const ticks = Array.from({ length: 5 }, (_, i) => y1 - ((y1 - y0) / 4) * i);
  const mean = usable.reduce((a, r) => a + (r.value ?? 0), 0) / usable.length;

  // Sparse tick labels, same rule as the MUI charts: 30 dates cannot all be shown.
  // everyNth returns a PREDICATE (value, index) => boolean, not a step count —
  // treating it as a number made `i % every` NaN and silently drew no labels at all.
  const showTick = allTicks ? () => true : everyNth(usable.length);

  return (
    <Box sx={{ minWidth: 0 }}>
    <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
      {/* Axis, drawn once beside the plot rather than per candle. */}
      <Box sx={{ position: 'relative', width: 34, height, flexShrink: 0 }}>
        {ticks.map((t) => (
          <Typography key={t} variant="caption" sx={{
            position: 'absolute', right: 0, top: `${pct(t)}%`,
            transform: 'translateY(-50%)', fontSize: 9, color: 'text.secondary',
          }}>
            {fmtNum(t, 0)}
          </Typography>
        ))}
      </Box>

      <Box sx={{ position: 'relative', flex: 1, height, minWidth: 0 }}>
        {/* Period mean, so a candle is read against something. */}
        <Box sx={{ position: 'absolute', left: 0, right: 0, top: `${pct(mean)}%`,
                   borderTop: '1px dashed', borderColor: 'divider' }} />
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', gap: '2px' }}>
          {usable.map((r, i) => {
            const label = r.bucket || r.day;
            const f = footer ? footer(r, i) : null;
            const title = `${label} — mean ${fmtNum(r.value, digits)} ${unit}`
              + ` · min ${fmtNum(r.min_v, digits)} · max ${fmtNum(r.max_v, digits)} ${unit}`
              + (f?.text ? ` · ${footerLabel || ''} ${f.text}` : '')
              + (r.samples ? ` · ${fmtInt(r.samples)} samples` : '');
            return (
              <Tooltip key={label} title={title} followCursor>
                <Box sx={{ flex: 1, position: 'relative', minWidth: 0, cursor: 'help',
                           '&:hover .cd-body': { opacity: 1 } }}>
                  {/* A BAR from min to max, not a hairline. At three pixels wide it
                      read as a cross rather than a range. */}
                  <Box sx={{
                    position: 'absolute', left: '22%', right: '22%',
                    top: `${pct(r.max_v)}%`, bottom: `${100 - pct(r.min_v)}%`,
                    bgcolor: colour, opacity: 0.45, borderRadius: '2px',
                  }} />
                  {/* mean, spanning the full bar so it reads as a level */}
                  {r.value != null && (
                    <Box sx={{
                      position: 'absolute', left: '14%', right: '14%',
                      top: `${pct(r.value)}%`, height: 3, bgcolor: colour,
                      transform: 'translateY(-1.5px)', borderRadius: '1px',
                    }} />
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* THE DEVIATION LINE, drawn across the means.
            Deviation is (mean - fleet mean) / fleet mean, and the fleet mean is a
            constant — so a line through the means has exactly the shape a deviation
            line would, and needs no second axis to carry a per cent on a degrees
            scale. Same curve, one scale, and the figures are printed underneath. */}
        {line && usable.length > 1 && (
          <Box component="svg" viewBox="0 0 100 100" preserveAspectRatio="none"
               sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
                     pointerEvents: 'none', overflow: 'visible' }}>
            <polyline
              points={usable.map((r, i) =>
                `${((i + 0.5) / usable.length) * 100},${pct(r.value)}`).join(' ')}
              fill="none" stroke={lineColour} strokeWidth="0.5"
              vectorEffect="non-scaling-stroke" />
            {usable.map((r, i) => (
              <circle key={r.bucket || r.day}
                      cx={((i + 0.5) / usable.length) * 100} cy={pct(r.value)}
                      r="2.5" fill={lineColour}
                      vectorEffect="non-scaling-stroke" />
            ))}
          </Box>
        )}
      </Box>
    </Box>

    <Box sx={{ display: 'flex', gap: 1, minWidth: 0, mt: 0.5 }}>
      <Box sx={{ width: 34, flexShrink: 0 }} />
      <Box sx={{ flex: 1, display: 'flex', gap: '2px', minWidth: 0 }}>
        {usable.map((r, i) => (
          <Box key={r.bucket || r.day} sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary' }}>
              {showTick(null, i) ? (allTicks ? (r.day || r.bucket || '')
                                             : (r.day || r.bucket || '').slice(5)) : ''}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>

    {/* A quantity the candle cannot carry, printed under its own column rather than
        drawn as a second series. Deviation is a PER CENT and the axis is degrees —
        a line would have to borrow a scale it does not belong to, and its height
        would mean nothing. As a labelled row it is exact and unambiguous. */}
    {footer && (
      <Box sx={{ display: 'flex', gap: 1, minWidth: 0, mt: 0.25 }}>
        <Typography variant="caption" sx={{ width: 34, flexShrink: 0, fontSize: 8.5,
                                            color: 'text.disabled', textAlign: 'right' }}>
          {footerLabel}
        </Typography>
        <Box sx={{ flex: 1, display: 'flex', gap: '2px', minWidth: 0 }}>
          {usable.map((r, i) => {
            const f = footer(r, i);
            return (
              <Box key={r.bucket || r.day} sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 9, fontWeight: 600, color: f?.colour || 'text.secondary' }}>
                  {f?.text ?? ''}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    )}
    </Box>
  );
}


function StationTempChart({ rows, unit = '°C', peakUnit, peakLabel = 'Maximum',
                            valueLabel = 'Mean', digits = 2 }) {
  const pUnit = peakUnit || unit;
  if (!rows.length) return null;
  const means = rows.map((r) => r.mean_v).filter((v) => v != null);
  const fleet = means.reduce((a, v) => a + v, 0) / means.length;

  // Padded to the data, not based at zero: these sit between 14 and 25 degrees and a
  // zero-based axis would squeeze every difference the chart exists to show.
  const lo = Math.floor(Math.min(...means) - 2);
  const hi = Math.ceil(Math.max(...rows.map((r) => r.max_v ?? r.mean_v)) + 1);
  const pos = (v) => ((v - lo) / (hi - lo)) * 100;
  const ticks = Array.from({ length: 6 }, (_, i) => lo + ((hi - lo) / 5) * i);

  return (
    <Box>
      <Stack spacing={1.25}>
        {rows.map((r) => {
          const dev = r.deviation_pct ?? 0;
          const colour = devColour(dev, { warn: 3, bad: 6 });
          const skew = r.p50 != null && Math.abs(r.p50 - r.mean_v) > 0.5
            ? ` · median ${fmtNum(r.p50, digits)} ${unit}, so the distribution is skewed`
            : '';
          const title = `${r.display_name} — ${valueLabel.toLowerCase()} `
            + `${fmtNum(r.mean_v, digits)} ${unit}, `
            + `${peakLabel.toLowerCase()} ${fmtNum(r.max_v, digits)} ${pUnit} · `
            + `${dev > 0 ? '+' : ''}${fmtNum(dev, 2)}% vs the fleet `
            + `${fmtNum(fleet, digits)} ${unit}${skew}`;
          return (
            <Tooltip key={r.parent} title={title} followCursor>
              <Stack direction="row" alignItems="center" spacing={1}
                     sx={{ cursor: 'help' }}>
                <Typography variant="caption" sx={{
                  width: 104, flexShrink: 0, fontSize: 11, textAlign: 'right',
                  color: 'text.secondary',
                }}>
                  {r.display_name}
                </Typography>

                <Box sx={{ flex: 1, position: 'relative', height: 20 }}>
                  {/* Fleet mean, so a bar's length is read against something. */}
                  <Box sx={{
                    position: 'absolute', left: `${pos(fleet)}%`, top: 0, bottom: 0,
                    borderLeft: '1px dashed', borderColor: 'divider',
                  }} />
                  {/* Mean, coloured by deviation. */}
                  <Box sx={{
                    position: 'absolute', left: 0, top: 4, height: 12,
                    width: `${pos(r.mean_v)}%`,
                    bgcolor: colour, opacity: 0.8,
                    borderRadius: '0 2px 2px 0',
                  }} />
                  {/* Maximum, as a tick on the same axis. */}
                  <Box sx={{
                    position: 'absolute', top: 1, left: `${pos(r.max_v)}%`,
                    width: 2, height: 18, bgcolor: MUTED,
                  }} />
                </Box>

                {[[r.mean_v, colour, 600], [r.max_v, 'text.secondary', 400]].map(
                  ([v, c, w], i) => (
                    <Typography key={i} variant="caption" sx={{
                      width: 54, flexShrink: 0, fontSize: 11, textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums', color: c, fontWeight: w,
                    }}>
                      {fmtNum(v, digits)}
                    </Typography>
                  ))}
              </Stack>
            </Tooltip>
          );
        })}
      </Stack>

      {/* Axis, so the bars are read against degrees and not only each other. */}
      <Box sx={{ ml: '112px', mr: '116px', mt: 0.5, position: 'relative', height: 14 }}>
        {ticks.map((t) => (
          <Typography key={t} sx={{
            position: 'absolute', left: `${pos(t)}%`, transform: 'translateX(-50%)',
            fontSize: 9, color: 'text.disabled',
          }}>
            {fmtNum(t, 0)}
          </Typography>
        ))}
      </Box>

      <Stack direction="row" spacing={2} sx={{ ml: '112px', mt: 0.75, flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={0.6} alignItems="center">
          <Box sx={{ width: 14, height: 8, bgcolor: MUTED, opacity: 0.8, borderRadius: '2px' }} />
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
            {`${valueLabel}, shaded by deviation`}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.6} alignItems="center">
          <Box sx={{ width: 2, height: 12, bgcolor: MUTED }} />
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
            {`${peakLabel}${pUnit === unit ? '' : ` (${pUnit})`}`}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
          {`Dashed line: fleet ${fmtNum(fleet, digits)} ${unit} · hover for the deviation`}
        </Typography>
      </Stack>
    </Box>
  );
}


/** Wind and relative humidity, which share one page in the operator's deck and one
 *  here. Same construction as the irradiance page: a bar to the station mean shaded
 *  by deviation, a tick at the maximum, deviation on hover.
 *
 *  Wind direction is carried in the group but not charted. A mean bearing is
 *  meaningless — 350 degrees and 10 degrees average to due south — and a wind rose
 *  is a different chart than this page is built for. */
function WindHumidityView({ byStation, byDay, basis, loading, freq }) {
  const rows = (metric) => byStation
    .filter((r) => r.data === metric)
    .sort((a, b) => a.display_name.localeCompare(b.display_name))
    .map((r) => ({ parent: `${r.parent}-${r.data}`, display_name: r.display_name,
                   mean_v: r.mean_v, max_v: r.max_v,
                   deviation_pct: r.deviation_pct, p50: null }));
  const series = (metric) => byDay.filter((r) => r.data === metric);

  if (loading) return <Skeleton height={520} />;
  const wind = rows('wind_speed');
  const hum = rows('relative_humidity');
  if (!wind.length && !hum.length) {
    return (
      <SectionCard title="Wind & humidity">
        <Typography variant="body2" color="text.secondary">
          No wind or humidity data in this period.
        </Typography>
      </SectionCard>
    );
  }

  const dayChart = (metric, title, unit, colour, caption) => {
    const d = series(metric);
    const labels = d.map((r) => r.bucket || r.day);
    return (
      <SectionCard title={title} caption={caption} sx={{ minWidth: 0 }}>
        <BarChart height={220}
          xAxis={[{ scaleType: 'band', data: labels,
                    valueFormatter: (v, ctx) => (ctx?.location === 'tick'
                      ? tickLabel(v, freq) : v),
                    tickLabelInterval: everyNth(labels.length),
                    tickLabelStyle: { fontSize: 9 } }]}
          series={[{ data: d.map((r) => r.value), label: unit, color: colour }]}
          margin={{ top: 12, right: 12, bottom: 28, left: 46 }}
        />
      </SectionCard>
    );
  };

  const avg = (xs) => (xs.length ? xs.reduce((a, x) => a + (x.mean_v || 0), 0) / xs.length : null);

  return (
    <>
      <Box sx={{
        display: 'grid', gap: 1.5,
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      }}>
        <KpiCard label="Mean wind" tooltip={basis}
                 value={`${fmtNum(avg(wind), 2)} m/s`} />
        <KpiCard label="Peak wind"
                 value={`${fmtNum(Math.max(...wind.map((x) => x.max_v ?? 0)), 2)} m/s`}
                 tooltip="Highest single sample. The operator's figure is lower because theirs is the peak of a smoothed series, not of raw samples." />
        <KpiCard label="Mean humidity" tooltip={basis}
                 value={`${fmtNum(avg(hum), 1)}%`} />
        <KpiCard label="Peak humidity"
                 value={`${fmtNum(Math.max(...hum.map((x) => x.max_v ?? 0)), 2)}%`} />
      </Box>

      <Box sx={{
        display: 'grid', gap: 1.5,
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      }}>
        <SectionCard title="Wind speed by station (m/s)" sx={{ minWidth: 0 }}
                     caption={`${basis} Bar is the station mean shaded by deviation from the fleet, tick the maximum. Hover for the deviation.`}>
          <StationTempChart rows={wind} unit="m/s" valueLabel="Mean"
                            peakLabel="Maximum" digits={2} />
        </SectionCard>
        <SectionCard title="Relative humidity by station (%)" sx={{ minWidth: 0 }}
                     caption={`${basis} Maxima sit at essentially 100% on every station, so the maximum separates nothing here — the mean and its deviation carry the signal.`}>
          <StationTempChart rows={hum} unit="%" valueLabel="Mean"
                            peakLabel="Maximum" digits={2} />
        </SectionCard>
      </Box>

      <Box sx={{
        display: 'grid', gap: 1.5,
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      }}>
        {dayChart('wind_speed', 'Wind speed by bucket (m/s)', 'm/s', '#3b82f6',
                  'Mean across the five stations, daylight only.')}
        {dayChart('relative_humidity', 'Relative humidity by bucket (%)', '%', '#8b5cf6',
                  'Mean across the five stations, daylight only. Daylight matters more here than anywhere else on these pages: humidity peaks overnight, so a 24-hour mean reads about 13 points higher and describes air no module ever worked in.')}
      </Box>
    </>
  );
}


/** Irradiation and peak irradiance per station, plus the daily series per station.
 *
 *  Same treatment as the temperature page, which is the one that reads best here
 *  too: a bar to the total shaded by its deviation from the fleet, a tick at the
 *  peak, and the deviation figure on hover rather than as a third chart.
 *
 *  The daily charts draw one line PER STATION rather than the fleet mean. That is
 *  what makes a failing sensor visible instead of averaged away — station 1 tracks
 *  the other four until about 25 June and then reads 7.2 kWh/m2 against their
 *  1.8-2.2, which a mean turns into a mild bump. The operator's own daily chart
 *  plots station 1 alone, so their series carries that fault as though it were the
 *  weather: it shows 8.1 kWh/m2 of GHI on 26 June, more than clear sky delivers at
 *  this latitude in midwinter, while their own POA that day reads 6.7. */
function IrradianceView({ irr, loading, freq }) {
  const st = useMemo(() => irr?.by_station || [], [irr]);
  const perStation = useMemo(() => irr?.by_station_bucket || [], [irr]);
  const buckets = useMemo(
    () => [...new Set(perStation.map((r) => r.bucket))].sort(), [perStation]);
  const names = useMemo(
    () => [...new Set(perStation.map((r) => r.display_name))].sort(), [perStation]);
  const lookup = useMemo(() => new Map(
    perStation.map((r) => [`${r.display_name}|${r.bucket}`, r])), [perStation]);

  if (loading) return <Skeleton height={520} />;
  if (!st.length) {
    return (
      <SectionCard title="Irradiance">
        <Typography variant="body2" color="text.secondary">
          No irradiance data in this period.
        </Typography>
      </SectionCard>
    );
  }

  const ticks = {
    valueFormatter: (v, ctx) => (ctx?.location === 'tick' ? tickLabel(v, freq) : v),
    tickLabelInterval: everyNth(buckets.length), tickLabelStyle: { fontSize: 9 },
  };
  const lineSeries = (key) => names.map((n, i) => ({
    label: n, showMark: false,
    color: ['#eab308', MUTED, '#3b82f6', '#8b5cf6', GOOD][i % 5],
    data: buckets.map((b) => lookup.get(`${n}|${b}`)?.[key] ?? null),
  }));

  // One row per metric: total, peak, deviation-by-colour. Rendered by the same
  // component the temperature page uses, fed the matching fields.
  const asRows = (total, peak, dev) => st.map((x) => ({
    parent: x.parent, display_name: x.display_name,
    mean_v: x[total], max_v: x[peak], deviation_pct: x[dev], p50: null,
  }));

  return (
    <>
      <Box sx={{
        display: 'grid', gap: 1.5,
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      }}>
        <KpiCard label="GHI irradiation" tooltip={irr?.basis}
                 value={`${fmtNum(st.reduce((a, x) => a + (x.ghi_kwh_m2 || 0), 0) / st.length, 0)} kWh/m²`} />
        <KpiCard label="POA irradiation" tooltip={irr?.basis}
                 value={`${fmtNum(st.reduce((a, x) => a + (x.poa_kwh_m2 || 0), 0) / st.length, 0)} kWh/m²`} />
        <KpiCard label="Peak GHI"
                 value={`${fmtNum(Math.max(...st.map((x) => x.ghi_max ?? 0)), 0)} W/m²`} />
        <KpiCard label="Peak POA"
                 value={`${fmtNum(Math.max(...st.map((x) => x.poa_max ?? 0)), 0)} W/m²`} />
      </Box>

      <Box sx={{
        display: 'grid', gap: 1.5,
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      }}>
        <SectionCard title="GHI by station" sx={{ minWidth: 0 }}
                     caption={`${irr?.basis} Bar is the period irradiation in kWh/m², shaded by deviation from the fleet; the tick is the peak irradiance in W/m². Hover for the deviation.`}>
          <StationTempChart rows={asRows('ghi_kwh_m2', 'ghi_max', 'ghi_dev_pct')}
                            unit="kWh/m²" peakUnit="W/m²" valueLabel="Irradiation"
                            peakLabel="Peak irradiance" digits={1} />
        </SectionCard>
        <SectionCard title="POA by station" sx={{ minWidth: 0 }}
                     caption={`${irr?.basis} Same construction as GHI. POA exceeds GHI because the modules track, so the plane of array sees more than the horizontal.`}>
          <StationTempChart rows={asRows('poa_kwh_m2', 'poa_max', 'poa_dev_pct')}
                            unit="kWh/m²" peakUnit="W/m²" valueLabel="Irradiation"
                            peakLabel="Peak irradiance" digits={1} />
        </SectionCard>
      </Box>

      <SectionCard title="GHI irradiation by station and bucket (kWh/m²)"
                   caption="One line per station, not their mean — a sensor drifting away from the others is the thing worth seeing, and an average hides it.">
        <LineChart height={250}
          xAxis={[{ scaleType: 'point', data: buckets, ...ticks }]}
          series={lineSeries('ghi_kwh_m2')}
          margin={{ top: 12, right: 12, bottom: 28, left: 46 }}
        />
      </SectionCard>

      <SectionCard title="POA irradiation by station and bucket (kWh/m²)"
                   caption="One line per station.">
        <LineChart height={250}
          xAxis={[{ scaleType: 'point', data: buckets, ...ticks }]}
          series={lineSeries('poa_kwh_m2')}
          margin={{ top: 12, right: 12, bottom: 28, left: 46 }}
        />
      </SectionCard>
    </>
  );
}


/** Ambient temperature, to the four figures the operator's slide fills. Its
 *  back-of-module column is blank on their own page too — that sensor does not
 *  exist — so this page states the absence rather than leaving four empty charts. */
function TemperatureView({ byStation, byDay, basis, loading }) {
  // No axis config here any more: RangeCandles draws its own scale and labels,
  // because a box plot's vertical extent is data rather than a bar's length.
  const st = [...byStation].sort((a, b) => a.display_name.localeCompare(b.display_name));
  const coolest = st.reduce(
    (a, x) => ((x.mean_v ?? Infinity) < (a?.mean_v ?? Infinity) ? x : a), null);

  if (loading) return <Skeleton height={480} />;
  if (!st.length) {
    return (
      <SectionCard title="Ambient temperature">
        <Typography variant="body2" color="text.secondary">No ambient data in this period.</Typography>
      </SectionCard>
    );
  }

  return (
    <>
      <Box sx={{
        display: 'grid', gap: 1.5,
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      }}>
        <KpiCard label="Mean ambient" tooltip={basis}
                 value={`${fmtNum(st.reduce((a, x) => a + (x.mean_v || 0), 0) / st.length, 2)} °C`} />
        <KpiCard label="Maximum"
                 value={`${fmtNum(Math.max(...st.map((x) => x.max_v ?? -Infinity)), 2)} °C`} />
        <KpiCard label="Coolest station"
                 value={coolest ? `${fmtNum(coolest.mean_v, 2)} °C` : '—'}
                 tooltip={coolest ? coolest.display_name : undefined}
                 accent={coolest && Math.abs(coolest.deviation_pct ?? 0) > 5 ? WARN : undefined} />
        <KpiCard label="Stations" value={String(st.length)} />
      </Box>

      <SectionCard title="Ambient temperature by station (°C)"
                   caption={`${basis} A bar per station from its coldest to its warmest reading, with the mean as a rule across it. The green line joins the means, which IS the deviation curve — deviation is the mean over a constant fleet mean, so the two have the same shape and this needs no second axis to put a per cent on a degrees scale. The figures are printed underneath.`}>
        <RangeCandles
          rows={st.map((x) => ({
            day: x.display_name.replace('MET Station ', 'MET '),
            value: x.mean_v, min_v: x.min_v, max_v: x.max_v,
            p25: x.p25, p50: x.p50, p75: x.p75,
            samples: x.hours ? Math.round(x.hours * 12) : null,
          }))}
          unit="°C" digits={2} colour={AMBER} height={240} allTicks line
          footerLabel="dev"
          footer={(_r, i) => {
            const d = st[i]?.deviation_pct;
            if (d == null) return null;
            return { text: `${d > 0 ? '+' : ''}${fmtNum(d, 1)}%`,
                     colour: devColour(d, { warn: 3, bad: 6 }) };
          }} />
      </SectionCard>

      <SectionCard title="Ambient temperature range by bucket (°C)"
                   caption="One candle per bucket: the bar spans the coldest to the warmest reading anywhere on site and the bright rule is the time-weighted mean. Dashed line is the period mean. Daylight only, so the low end is dawn rather than night. Not zero-based — nothing here is a length from the axis, each mark is a position on a shared scale.">
        <RangeCandles rows={byDay} unit="°C" digits={2} colour={AMBER} />
      </SectionCard>
    </>
  );
}


function SensorsPanel({ cap, period, setPeriod, presets }) {
  const [group, setGroup] = useState('soiling');
  const { data, loading, error, refresh } = useScadaPanel('sensors', cap, { group }, period);
  // Memoised: `data?.x || []` yields a new array identity every render, which
  // would defeat every useMemo downstream.
  const byStation = useMemo(() => data?.sensors?.by_station || [], [data]);
  const byDay = useMemo(() => data?.sensors?.by_day || [], [data]);
  const soil = data?.soiling;
  const freq = period?.frequency;
  // Stated by the query itself, so the caption cannot drift from the method.
  const basis = data?.sensors?.basis || '';

  const metrics = useMemo(
    () => [...new Set(byDay.map((r) => r.data))], [byDay],
  );
  const dayLabels = useMemo(
    () => [...new Set(byDay.map((r) => r.bucket || r.day))].sort(), [byDay],
  );

  return (
    <>
      <PanelHeader title="Sensors" cap={cap} onRefresh={refresh}
        period={period} onPeriodChange={setPeriod} presets={presets}
        showFrequency granularity="5min"
        afterTitle={(
          <ToggleButtonGroup size="small" exclusive value={group}
            onChange={(_, v) => v && setGroup(v)}
            sx={{ ml: 1, flexWrap: 'wrap' }}>
            {SENSOR_GROUPS.map((g) => (
              <ToggleButton key={g.key} value={g.key} sx={{ textTransform: 'none' }}>
                {g.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={2}>
        {group === 'soiling'
          ? <SoilingView soil={soil} loading={loading} freq={freq} />
          : group === 'temperature'
          ? <TemperatureView byStation={byStation} byDay={byDay} basis={basis}
                             loading={loading} freq={freq} />
          : group === 'irradiance'
          ? <IrradianceView irr={data?.irradiance} loading={loading} freq={freq} />
          : group === 'wind'
          ? <WindHumidityView byStation={byStation} byDay={byDay} basis={basis}
                              loading={loading} freq={freq} />
          : <>
        <SectionCard title="Station disagreement"
          caption={`Deviation from the station-fleet mean — the shape that exposes a miscalibrated sensor, flagged beyond ±2%. Performance ratio is computed against the nearest station, so a biased sensor biases every inverter in its zone. ${basis}`}>
          {loading ? <Skeleton height={200} /> : (
            <EmphasisBars threshold={2} rows={byStation.map((r) => ({
              label: `${r.display_name} · ${r.data.replace('irradiance_', '').replace('_', ' ')}`,
              value: r.deviation_pct ?? 0,
            }))} />
          )}
        </SectionCard>

        <SectionCard title="By station" caption={`Mean, max and deviation from the station-fleet mean. ${basis}`}>
          {loading ? <Skeleton height={160} /> : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Metric', 'Station', 'Mean', 'Max', 'Deviation'].map((h) =>
                      <TableCell key={h}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byStation.map((r) => (
                    <TableRow key={`${r.data}-${r.parent}`}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.data}</TableCell>
                      <TableCell>{r.display_name}</TableCell>
                      <TableCell>{fmtNum(r.mean_v, 2)}</TableCell>
                      <TableCell>{fmtNum(r.max_v, 2)}</TableCell>
                      <TableCell sx={{ color: devColour(r.deviation_pct), fontWeight: 600 }}>
                        {r.deviation_pct == null ? '—'
                          : `${r.deviation_pct > 0 ? '+' : ''}${fmtNum(r.deviation_pct, 2)}%`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>

        <SectionCard title="By day"
                     caption={metrics.join(' · ') || undefined}>
          {loading ? <Skeleton height={240} /> : (
            <LineChart height={260}
              xAxis={[{ scaleType: 'point', data: dayLabels,
                        valueFormatter: (v, ctx) => (ctx?.location === 'tick'
                          ? tickLabel(v, freq) : v),
                        tickLabelInterval: everyNth(dayLabels.length),
                        tickLabelStyle: { fontSize: 9 } }]}
              series={metrics.map((m, i) => ({
                label: m,
                color: [AMBER, MUTED, '#3b82f6', '#8b5cf6'][i % 4],
                showMark: false,
                data: dayLabels.map((d) =>
                  byDay.find((r) => (r.bucket || r.day) === d && r.data === m)?.value ?? null),
              }))}
              margin={{ top: 12, right: 12, bottom: 28, left: 52 }}
            />
          )}
        </SectionCard>

          </>}

      </Stack>
    </>
  );
}


// ── availability — deck slide 11 ────────────────────────────────────────────

/** Priority order, which is also the cascade order: a loss is booked at the highest
 *  level that was down, so a feeder outage is not charged again to the inverters
 *  behind it. `site_outage` sits above the operator's four — they book a whole-plant
 *  stop by hand as an external event, we derive it from telemetry. */
const EPLE_CLASSES = [
  { key: 'site_outage', label: 'Whole plant', colour: '#7c3aed' },
  { key: 'mv_feeder', label: 'MV feeder', colour: '#e11d48' },
  { key: 'inverter', label: 'Inverter', colour: '#f59e0b' },
  { key: 'combiner_box', label: 'Combiner box', colour: '#3b82f6' },
];

/** Availability bands. One definition drives both the bar colours and the key
 *  above them, so a threshold can never be changed in one place only.
 *
 *  These are reading aids, not contractual lines — no availability guarantee exists
 *  in plant_parameter, so nothing here should be read as pass or fail. */
const AVAIL_BANDS = [
  { key: 'good', label: '≥ 99%', colour: GOOD, min: 0.99 },
  { key: 'warn', label: '95–99%', colour: WARN, min: 0.95 },
  { key: 'bad', label: '< 95%', colour: BAD, min: -Infinity },
];
const AVAIL_UNKNOWN = { key: 'unknown', label: 'unknown', colour: MUTED, min: null };

function availBand(v) {
  // Unknown is its own colour, not the worst band: a bucket where nothing reported
  // has no availability, and painting it red would assert an outage we never saw.
  if (v == null) return AVAIL_UNKNOWN;
  return AVAIL_BANDS.find((b) => v >= b.min) || AVAIL_BANDS[AVAIL_BANDS.length - 1];
}


/** EPLE by level as a donut, in the cards panel. Shares can be read off it because
 *  the priority cascade books each loss once, so the parts really are parts of a
 *  whole — the exclusion mix next door cannot say that of itself. */
function EpleComposition({ byLevel, total }) {
  // EVERY level is listed, including the ones at zero. A level that lost nothing is
  // a real and reportable result — dropping it from the legend made combiner box
  // look absent rather than clean, and left the reader unable to tell "no loss"
  // from "not measured". Only the ARC data is filtered, because a zero-value slice
  // draws no arc but still consumes a padding gap.
  const parts = EPLE_CLASSES.map((c) => ({ ...c, mwh: byLevel?.[c.key] || 0 }));
  const arcs = parts.filter((c) => c.mwh > 0);
  const sum = parts.reduce((a, c) => a + c.mwh, 0);
  if (!sum) {
    return (
      <Typography variant="body2" color="text.secondary">
        No lost energy in this period.
      </Typography>
    );
  }
  return (
    <Stack spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <PieChart
        width={132} height={132}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        hideLegend
        series={[{
          data: arcs.map((c) => ({ id: c.key, value: c.mwh,
                                   label: c.label, color: c.colour })),
          innerRadius: 36, outerRadius: 62, paddingAngle: 1.5, cornerRadius: 2,
          highlightScope: { faded: 'global', highlighted: 'item' },
          faded: { innerRadius: 36, additionalRadius: -2, color: 'gray' },
          valueFormatter: (item) =>
            `${fmtNum(item?.value ?? 0, 2)} MWh · ${fmtPct((item?.value ?? 0) / sum, 1)}`,
        }]}
      />
      <Stack spacing={0.35} sx={{ minWidth: 0, width: '100%' }}>
        {parts.map((c) => (
          <Stack key={c.key} direction="row" spacing={0.75} alignItems="center">
            {/* Hollow swatch at zero: the level is listed and its colour is still
                identifiable, but nothing is claimed to be filled in. */}
            <Box sx={{ width: 9, height: 9, borderRadius: '2px', flexShrink: 0,
                       bgcolor: c.mwh > 0 ? c.colour : 'transparent',
                       border: c.mwh > 0 ? 'none' : '1px solid',
                       borderColor: c.colour }} />
            <Typography sx={{ fontSize: 11,
                              color: c.mwh > 0 ? 'text.secondary' : 'text.disabled' }}>
              {c.mwh > 0
                ? `${c.label} — ${fmtPct(c.mwh / sum, 1)} (${fmtNum(c.mwh, 1)} MWh)`
                : `${c.label} — none`}
            </Typography>
          </Stack>
        ))}
        <Typography sx={{ fontSize: 10, color: 'text.disabled', pt: 0.25 }}>
          {`${fmtNum(total ?? sum, 1)} MWh total`}
        </Typography>
      </Stack>
    </Stack>
  );
}


function AvailabilityPanel({ cap, period, setPeriod, presets }) {
  const { data, loading, error, refresh } = useScadaPanel('availability', cap, null, period);
  const p = data?.availability?.period || {};
  const buckets = useMemo(() => data?.availability?.buckets || [], [data]);
  const byLevel = p.eple_by_level || {};
  const eplTotal = p.eple_total;
  const freq = period?.frequency;
  const labels = buckets.map((b) => b.bucket);
  const lvl = (b, key) => (b.eple_by_level || {})[key] ?? 0;

  // Legend filtering on the stacked chart. This is what the four single-level
  // charts used to do, minus four sets of axes and a fixed shared scale.
  const [hiddenLevels, setHiddenLevels] = useState(() => new Set());
  const toggleLevel = (key) => setHiddenLevels((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const shownLevels = EPLE_CLASSES.filter((c) => !hiddenLevels.has(c.key));

  // Only the bands that actually occur, so the legend documents the month in hand
  // rather than listing categories nothing fell into.
  const bandsPresent = useMemo(() => {
    const hit = new Set(buckets.map((b) => availBand(b.om_availability).key));
    return [...AVAIL_BANDS, AVAIL_UNKNOWN].filter((b) => hit.has(b.key));
  }, [buckets]);

  const axis = {
    scaleType: 'band', data: labels,
    valueFormatter: (v, ctx) => (ctx?.location === 'tick' ? tickLabel(v, freq) : v),
    tickLabelInterval: everyNth(labels.length),
    tickLabelStyle: { fontSize: 9 },
  };

  // The operator's hourly pages. One window shared by all six grids so a dark
  // band in one sits at the same height as the same hour in the others — derived
  // from where the plant actually generated, then padded an hour either side,
  // which lands on their own 06:00-17:00 for June.
  const cells = useMemo(() => (data?.availability?.hourly || []).map((c) => ({
    ...c,
    // HourGrid renders the raw number beside the unit, so the ratio is scaled here
    // rather than showing "0.98 %".
    availability_pct: c.availability == null ? null : c.availability * 100,
  })), [data]);
  const hourWindow = useMemo(() => {
    const live = cells.filter((c) => Math.abs(c.epme || 0) > 0.5 || (c.eple_total || 0) > 0.05)
      .map((c) => c.hour);
    if (!live.length) return undefined;
    return [Math.max(0, Math.min(...live) - 1), Math.min(23, Math.max(...live) + 1)];
  }, [cells]);

  const hourGrid = (title, field, colour, opts = {}) => (
    <SectionCard key={field} title={title} sx={{ minWidth: 0 }}
                 caption={opts.caption || `Hour of day down the left, dates across. ${
                   'Shading is the value itself; hover any cell for the figure.'}`}>
      {loading ? <Skeleton height={190} /> : (
        <HourGrid cells={cells} field={field} unit={opts.unit || 'MWh'}
                  colour={colour} hours={hourWindow}
                  domain={opts.domain} digits={opts.digits ?? 2} />
      )}
    </SectionCard>
  );

  return (
    <>
      <PanelHeader title="Availability" cap={cap} onRefresh={refresh}
        period={period} onPeriodChange={setPeriod} presets={presets}
        showFrequency granularity="daily" />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* The per-level numbers live in the donut now, so the cards carry only the
          period figures. Donut on the left at a fixed share, cards filling the
          rest on one line — the same arrangement as the other panels. */}
      <Box sx={{
        mb: 2, display: 'grid', gap: 1.5, alignItems: 'stretch', minWidth: 0,
        gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 22%) 1fr' },
      }}>
        <SectionCard title="Lost energy mix" sx={{ height: '100%', minWidth: 0 }}
                     caption="Each level's share of EPLE for the period. The shares are true parts of a whole because the priority cascade books every loss exactly once.">
          {loading ? <Skeleton height={220} />
            : <EpleComposition byLevel={byLevel} total={eplTotal} />}
        </SectionCard>

        {/* Two rows rather than one tall one: the cards stretch to the donut's
            height, and KpiCard pins its value to the bottom, so a single row left a
            large gap between every label and its number. EPLE spans the lower row —
            it is the donut's own subject, and it fills the space the tracker card
            used to occupy. */}
        <Box sx={{
          display: 'grid', gap: 1.5, minWidth: 0,
          gridTemplateColumns: 'repeat(2, 1fr)',
        }}>
          <KpiCard label="O&M availability" loading={loading}
                   value={fmtPct(p.availability, 2)}
                   tooltip={data?.availability?.basis} />
          <KpiCard label="EPME" loading={loading} value={`${fmtInt(p.epme)} MWh`}
                   tooltip="Measured plant energy over the window — the numerator of availability." />
          <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
            <KpiCard label="EPLE" loading={loading} value={`${fmtNum(eplTotal, 1)} MWh`}
                     tooltip="Expected production lost to unavailable equipment, after the priority cascade." />
          </Box>
        </Box>
      </Box>

      {/* The headline chart. Stacking is legitimate HERE and nowhere else on this
          screen: the priority cascade makes the four levels mutually exclusive
          within an interval, so they sum to EPLE with nothing double-counted. The
          exclusion-reason filters on the Performance screen overlap and must never
          be stacked this way. */}
      <SectionCard title="Lost energy by level (MWh)" sx={{ mb: 2, minWidth: 0 }}
                   caption="Every MWh of EPLE, stacked by the equipment level it attaches to. The segments add up to the EPLE total because the priority cascade books each loss once — a feeder outage is not charged again to the inverters behind it. Click a level to isolate it; the axis rescales to what is left.">
        {loading ? <Skeleton height={300} /> : (
          <>
            {/* Our own legend, because MUI's is not interactive. Toggling a level
                filters the stack, which is what replaced the four single-level
                charts that used to sit below. Colour stays keyed to the level, so
                hiding one never repaints the others. */}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap
                   justifyContent="center" sx={{ mb: 1 }}>
              {EPLE_CLASSES.map((c) => {
                const on = !hiddenLevels.has(c.key);
                return (
                  <Stack key={c.key} direction="row" spacing={0.75} alignItems="center"
                    onClick={() => toggleLevel(c.key)}
                    sx={{ cursor: 'pointer', opacity: on ? 1 : 0.4, userSelect: 'none',
                          '&:hover': { opacity: on ? 0.8 : 0.6 } }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px',
                               bgcolor: c.colour, flexShrink: 0,
                               // Hollow when off, so state is not colour-alone.
                               boxShadow: on ? 'none' : `inset 0 0 0 10px ${'rgba(0,0,0,0.55)'}`,
                               border: '1px solid', borderColor: c.colour }} />
                    <Typography sx={{ fontSize: 12,
                                      color: on ? 'text.primary' : 'text.disabled',
                                      textDecoration: on ? 'none' : 'line-through' }}>
                      {c.label}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
            {shownLevels.length ? (
              <BarChart height={320} hideLegend
                xAxis={[axis]}
                series={shownLevels.map((c) => ({
                  // `id` IS LOAD-BEARING. Without it MUI keys series by array
                  // index, so filtering one out shifts every later series onto the
                  // previous one's slot: hiding Inverter left its data on screen
                  // wearing Combiner box's blue. A stable id per level is what makes
                  // the filter honest — colour follows the level, never its position.
                  id: c.key,
                  data: buckets.map((b) => lvl(b, c.key)),
                  label: c.label, color: c.colour, stack: 'eple',
                  valueFormatter: (v) => `${fmtNum(v, 2)} MWh`,
                }))}
                margin={{ top: 12, right: 12, bottom: 28, left: 52 }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary"
                          sx={{ py: 6, textAlign: 'center' }}>
                Every level is hidden — pick one above.
              </Typography>
            )}
          </>
        )}
      </SectionCard>

      {/* EPME and availability on ONE plot, and deliberately NOT as a dual axis.
          Two y-scales let you slide MWh against per-cent until any correlation you
          like appears, so the two quantities share a single axis instead: bar HEIGHT
          is the energy, bar COLOUR is the availability band. Same device as the
          temperature chart, where colour carries deviation. Hover gives both
          figures exactly. */}
      <SectionCard title="Measured energy and availability" sx={{ mb: 2, minWidth: 0 }}
                   caption="Bar height is EPME — measured plant export for the bucket, the numerator of availability. Bar colour is that bucket's availability, EPME ÷ (EPME + EPLE): green at or above 99%, amber 95–99%, red below 95%, grey where no unit reported and availability is unknown. Hover for both figures.">
        {loading ? <Skeleton height={300} /> : (
          <BarChart height={320}
            xAxis={[axis]}
            /* Compact ticks: a monthly bucket runs to five figures and MUI
               ellipsises tick text itself, so "10,000" came out "10,0...". */
            yAxis={[{ valueFormatter: (v) => (Math.abs(v) >= 1000
                        ? `${fmtNum(v / 1000, 0)}k` : fmtNum(v, 0)) }]}
            /* ONE SERIES PER BAND, each carrying EPME only on the buckets in that
               band and null elsewhere. The obvious approach — a single series with
               an ordinal colorMap on the band axis — drew the bars against a
               different scale than its own axis: 570 MWh rendered 89px tall on an
               axis where 100 MWh is 42.5px. Series colour is reliable where that
               was not, MUI's own legend then documents the bands for free, and a
               bucket belongs to exactly one band so the stack never sums. */
            series={bandsPresent.map((band) => ({
              id: band.key,
              label: band.label,
              color: band.colour,
              stack: 'epme',
              data: buckets.map((b) => (availBand(b.om_availability) === band
                ? b.epme : null)),
              valueFormatter: (v, ctx) => {
                if (v == null) return null;
                const b = buckets[ctx?.dataIndex ?? -1];
                const a = b?.om_availability;
                return `${fmtNum(v, 2)} MWh · availability ${
                  a == null ? 'unknown' : fmtPct(a, 2)}`;
              },
            }))}
            margin={{ top: 12, right: 12, bottom: 28, left: 52 }}
          />
        )}
      </SectionCard>

      {data?.availability?.hourly_error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {data.availability.hourly_error}
        </Alert>
      )}

      <Box sx={{
        mb: 2, display: 'grid', gap: 2, minWidth: 0,
        gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
      }}>
        {hourGrid('Hourly EPME (MWh)', 'epme', MUTED, {
          caption: 'Measured plant energy per hour. Hour of day down the left, dates across — hover any cell for the figure. Verified against the operator hour by hour: 1 June reads 18.2 / 62.1 / 71.9 / 71.1 / 69.1 / 69.3 / 72.2 / 74.0 / 52.7 / 12.3 MWh from 07:00, matching their row exactly.',
        })}
        {hourGrid('Hourly availability (%)', 'availability_pct', BAD, {
          unit: '%', digits: 1, domain: [100, 90],
          caption: 'EPME / (EPME + EPLE) for the hour. Shaded by SHORTFALL, so a fully available hour is nearly blank and colour marks the losses. An hour with nothing expected and nothing lost is empty rather than 0% — a dark hour is not an unavailable one.',
        })}
        {hourGrid('Hourly EPLE MV feeder (MWh)', 'eple_mv_feeder', '#e11d48')}
        {hourGrid('Hourly EPLE inverter (MWh)', 'eple_inverter', '#f59e0b', {
          caption: 'Expected energy lost to inverter outages, per hour. Tracks the operator closely: 21 June reads 0.5 / 3.1 / 5.1 / 5.0 / 4.8 / 4.8 / 4.9 / 5.0 / 3.0 / 0.7 MWh from 07:00 against their 0.8 / 3.3 / 5.0 / 5.0 / 4.8 / 4.8 / 4.9 / 4.8 / 2.8 / 0.6.',
        })}
        {hourGrid('Hourly EPLE combiner box (MWh)', 'eple_combiner_box', '#3b82f6')}
        {hourGrid('Hourly EPLE whole plant (MWh)', 'eple_site_outage', '#7c3aed', {
          caption: 'Hours where every real inverter was dead and the plant exported nothing. The operator books these by hand as an external event and excludes them; we derive them from telemetry and keep them as their own category, because attribution between grid and plant is not derivable from the data.',
        })}
      </Box>

      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <SectionCard title="Hours with equipment down"
                     caption="Assessed hours in which at least one unit of the class was producing nothing. Not additive with EPLE: an hour with one inverter down and an hour with twenty both count once here.">
          {loading ? <Skeleton height={180} /> : (
            /* Driven off EPLE_CLASSES, not Object.entries: Flask sorts JSON keys
               alphabetically, which listed these combiner-box-first instead of in
               the cascade order every other control on this page uses. */
            <EmphasisBars threshold={9e9} unit=" h" signed={false}
              rows={EPLE_CLASSES
                .filter((c) => (p.down_hours || {})[c.key] != null)
                .map((c) => ({ label: c.label, value: p.down_hours[c.key] ?? 0 }))} />
          )}
        </SectionCard>

      </Stack>
    </>
  );
}


// ── inverters — deck slides 15, 16 ──────────────────────────────────────────

/** #rrggbb + alpha -> rgba(). Used where a fill needs to fade but its text must
 *  not: CSS `opacity` is inherited by children, `background-color` alpha is not. */
function hexA(hex, a) {
  const h = String(hex).replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a.toFixed(3)})`;
}


/** PCU x inverter grid — the operator's "Inverter Analysis by Inverter" layout, with
 *  PCUs across and the four inverter slots down, numbers in the cells.
 *
 *  `worseWhen` decides which end of the range gets the ink. On specific production
 *  and PR the reader is hunting the LOW cells, so shading by height would leave every
 *  underperformer pale and every healthy inverter dark — the opposite of useful.
 *  Outage hours are the other way round. */
function PcuGrid({ rows, field, unit, digits = 2, colour, worseWhen = 'low',
                  showTotals = false, scale = 1 }) {
  const { pcus, slots, cells, lo, hi } = useMemo(() => {
    const p = [...new Set(rows.map((r) => r.pcu))].filter((v) => v != null).sort((a, b) => a - b);
    const s = [...new Set(rows.map((r) => r.inverter))].filter((v) => v != null).sort((a, b) => a - b);
    const m = new Map();
    const vs = [];
    rows.forEach((r) => {
      const v = r[field];
      m.set(`${r.pcu}:${r.inverter}`, v);
      if (v != null) vs.push(v);
    });
    return { pcus: p, slots: s, cells: m,
             lo: vs.length ? Math.min(...vs) : 0, hi: vs.length ? Math.max(...vs) : 1 };
  }, [rows, field]);

  if (!pcus.length) return null;
  const span = (hi - lo) || 1;
  const ink = (v) => {
    if (v == null) return 0;
    const t = worseWhen === 'low' ? (hi - v) / span : (v - lo) / span;
    return Math.max(0.06, Math.min(1, t));
  };
  const sticky = { position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper' };
  const LABEL = 62;

  const totals = showTotals
    ? pcus.map((p) => slots.reduce((a, s) => a + (cells.get(`${p}:${s}`) || 0), 0))
    : null;

  return (
    <Box sx={{ overflowX: 'auto', maxWidth: '100%', minWidth: 0, pb: 1 }}>
      <Box sx={{ display: 'grid', width: '100%', gap: '2px',
                 gridTemplateColumns: `${LABEL}px repeat(${pcus.length}, minmax(46px, 1fr))` }}>
        <Typography variant="caption" sx={{ ...sticky, fontSize: 9, color: 'text.secondary',
                                           alignSelf: 'end', pr: 0.75, textAlign: 'right' }}>
          PCU →
        </Typography>
        {pcus.map((p) => (
          <Typography key={p} variant="caption" sx={{ fontSize: 9, color: 'text.secondary',
                                                     textAlign: 'center', alignSelf: 'end' }}>
            {p}
          </Typography>
        ))}

        {slots.map((s) => (
          <React.Fragment key={s}>
            <Typography variant="caption" sx={{ ...sticky, fontSize: 10, color: 'text.secondary',
                                               textAlign: 'right', pr: 0.75, lineHeight: '20px' }}>
              {`INV ${s}`}
            </Typography>
            {pcus.map((p) => {
              const v = cells.get(`${p}:${s}`);
              const has = v != null;
              return (
                <Tooltip key={p} title={has
                  ? `PCU ${p} inverter ${s} — ${fmtNum(v * scale, digits)} ${unit}`
                  : `PCU ${p} inverter ${s} — no such inverter`}>
                  <Box sx={{
                    height: 20, borderRadius: '2px', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    // ALPHA ON THE FILL, not opacity on the box. `opacity` is
                    // inherited by children, so it faded the number along with its
                    // background and made most of the grid unreadable.
                    bgcolor: has ? hexA(colour, ink(v)) : 'transparent',
                    border: has ? 'none' : '1px dashed',
                    borderColor: 'divider',
                  }}>
                    {/* Contrast is CHOSEN, not hoped for. The fill opacity spans
                        0.06 to 1, so a single ink colour is unreadable at one end
                        or the other: fixed dark text vanished on the faint cells,
                        which is most of the grid. Dark on strong fills, light on
                        faint ones. */}
                    <Typography sx={{ fontSize: 8.5, fontWeight: 600, lineHeight: 1,
                                      color: !has ? 'text.disabled'
                                        : ink(v) > 0.5 ? '#0b0b0b' : 'text.primary' }}>
                      {has ? fmtNum(v * scale, digits) : '—'}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}

        {totals && (
          <>
            <Typography variant="caption" sx={{ ...sticky, fontSize: 10, fontWeight: 700,
                                               textAlign: 'right', pr: 0.75, lineHeight: '20px' }}>
              Total
            </Typography>
            {totals.map((t, i) => (
              <Box key={pcus[i]} sx={{ height: 20, display: 'flex', alignItems: 'center',
                                       justifyContent: 'center' }}>
                <Typography sx={{ fontSize: 8.5, fontWeight: 700, lineHeight: 1,
                                  color: t > 0 ? BAD : 'text.disabled' }}>
                  {fmtNum(t * scale, digits)}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}


function InvertersPanel({ cap, period, setPeriod, presets }) {
  const { data, loading, error, refresh } = useScadaPanel('inverters', cap, null, period);
  const fleet = useMemo(() => data?.fleet?.inverters || [], [data]);
  const buckets = useMemo(() => data?.daily?.buckets || [], [data]);
  const excluded = data?.fleet?.excluded_slots || [];
  const freq = period?.frequency;
  const labels = buckets.map((b) => b.bucket);

  const axis = {
    scaleType: 'band', data: labels,
    valueFormatter: (v, ctx) => (ctx?.location === 'tick' ? tickLabel(v, freq) : v),
    tickLabelInterval: everyNth(labels.length),
    tickLabelStyle: { fontSize: 9 },
  };

  const stats = useMemo(() => {
    const sp = fleet.map((r) => r.specific_production).filter((v) => v != null);
    const pr = fleet.map((r) => r.performance_ratio).filter((v) => v != null);
    if (!sp.length) return {};
    const worst = fleet.reduce((a, b) =>
      (b.specific_production < a.specific_production ? b : a), fleet[0]);
    const downed = fleet.filter((r) => (r.outage_hours || 0) >= 1)
      .sort((a, b) => b.outage_hours - a.outage_hours);
    return {
      mean: sp.reduce((a, b) => a + b, 0) / sp.length,
      min: Math.min(...sp), max: Math.max(...sp),
      pr: pr.length ? pr.reduce((a, b) => a + b, 0) / pr.length : null,
      outage: fleet.reduce((a, r) => a + (r.outage_hours || 0), 0),
      worst, downed,
    };
  }, [fleet]);

  return (
    <>
      <PanelHeader title="Inverters" cap={cap} onRefresh={refresh}
        period={period} onPeriodChange={setPeriod} presets={presets}
        showFrequency granularity="daily" />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack sx={{
        mb: 2, display: 'grid', gap: 1.5,
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      }}>
        <KpiCard label="Fleet specific production" loading={loading}
                 value={`${fmtNum(stats.mean, 1)} kWh/kWp`}
                 tooltip="Mean across the 75 real inverters — energy over as-built DC capacity. Reproduces the operator's grid cell for cell." />
        <KpiCard label="Fleet PR" loading={loading} value={fmtPct(stats.pr, 2)}
                 tooltip={data?.fleet?.basis} />
        <KpiCard label="Outage hours" loading={loading}
                 value={`${fmtNum(stats.outage, 1)} h`}
                 accent={stats.outage > 0 ? WARN : undefined}
                 tooltip="AC power below 2 kW while POA exceeds 100 W/m², excluding curtailed and dispatch-capped periods." />
        <KpiCard label="Worst inverter" loading={loading}
                 value={stats.worst ? `${fmtNum(stats.worst.specific_production, 1)}` : '—'}
                 accent={BAD} tooltip={stats.worst?.display_name} />
        <KpiCard label="Inverters down ≥ 1 h" loading={loading}
                 value={stats.downed ? String(stats.downed.length) : '—'}
                 tooltip={stats.downed?.slice(0, 6).map((r) =>
                   `${r.display_name} ${fmtNum(r.outage_hours, 1)}h`).join(' · ')} />
      </Stack>

      {/* ── The operator's "by day" page ─────────────────────────────────── */}
      <Box sx={{
        mb: 2, display: 'grid', gap: 2, minWidth: 0,
        gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
      }}>
        <SectionCard title="Daily average inverter PR (%)" sx={{ minWidth: 0 }}
                     caption={data?.daily?.basis}>
          {loading ? <Skeleton height={200} /> : (
            <BarChart height={215} hideLegend
              xAxis={[axis]} yAxis={[{ max: 100 }]}
              series={[{ data: buckets.map((b) => (b.performance_ratio == null
                           ? null : b.performance_ratio * 100)),
                         label: 'PR', color: GOOD,
                         valueFormatter: (v) => (v == null ? 'no PR — too little unexcluded sun'
                           : `${fmtNum(v, 2)}%`) }]}
              margin={{ top: 12, right: 12, bottom: 28, left: 42 }}
            />
          )}
        </SectionCard>

        {/* Pairs with PR on the first row — both are single-series bar charts and
            read fine at half width, so they share a line rather than each taking
            one and leaving a hole beside it. */}
        <SectionCard title="Daily inverter outage hours" sx={{ minWidth: 0 }}
                     caption="Summed across the fleet. AC power below 2 kW while POA exceeds 100 W/m², excluding curtailed and dispatch-capped periods.">
          {loading ? <Skeleton height={200} /> : (
            <BarChart height={215} hideLegend
              xAxis={[axis]}
              series={[{ data: buckets.map((b) => b.outage_hours),
                         label: 'Outage h', color: BAD,
                         valueFormatter: (v) => `${fmtNum(v, 2)} h` }]}
              margin={{ top: 12, right: 12, bottom: 28, left: 42 }}
            />
          )}
        </SectionCard>

        {/* Full width on the row below: three series and two of them are lines,
            which need the horizontal room to be readable at 30 buckets. */}
        <SectionCard title="Daily average specific production"
                     sx={{ minWidth: 0, gridColumn: { lg: '1 / -1' } }}
                     caption="The operator's own three-series chart. Specific production is fleet energy over fleet as-built DC capacity — a ratio of sums, never a mean of per-inverter ratios. The curtailment and dispatch-cap hours are the periods excluded from PR and from outage hours. All three share ONE axis: their numeric ranges genuinely overlap here, so there is no second scale and therefore no scaling choice that could manufacture a correlation between them. The axis carries two units, which is why every series names its own on hover.">
          {loading ? <Skeleton height={240} /> : (
            <>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap
                     justifyContent="center" sx={{ mb: 1 }}>
                {[{ c: '#3b82f6', l: 'Specific production (kWh/kWp)' },
                  { c: exColour('curtailment_ramping'), l: 'Hours curtailed' },
                  { c: exColour('dispatch_flag_up'), l: 'Hours dispatch cap' }].map((x) => (
                  <Stack key={x.l} direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px',
                               bgcolor: x.c, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{x.l}</Typography>
                  </Stack>
                ))}
              </Stack>
              {/* ChartContainer rather than BarChart: mixing a bar series with two
                  line series on one axis needs the plots composed by hand. */}
              <ChartContainer height={260}
                xAxis={[{ ...axis, id: 'day' }]}
                yAxis={[{ id: 'shared', width: 44 }]}
                series={[
                  { type: 'bar', id: 'sp', yAxisId: 'shared', color: '#3b82f6',
                    label: 'Specific production',
                    data: buckets.map((b) => b.specific_production),
                    valueFormatter: (v) => (v == null ? '—' : `${fmtNum(v, 3)} kWh/kWp`) },
                  { type: 'line', id: 'curt', yAxisId: 'shared',
                    color: exColour('curtailment_ramping'),
                    label: 'Hours curtailed', showMark: false, curve: 'linear',
                    data: buckets.map((b) => b.hours_curtailed),
                    valueFormatter: (v) => (v == null ? '—' : `${fmtNum(v, 2)} h`) },
                  { type: 'line', id: 'disp', yAxisId: 'shared',
                    color: exColour('dispatch_flag_up'),
                    label: 'Hours dispatch cap', showMark: false, curve: 'linear',
                    data: buckets.map((b) => b.hours_dispatch_cap),
                    valueFormatter: (v) => (v == null ? '—' : `${fmtNum(v, 2)} h`) },
                ]}
                margin={{ top: 12, right: 12, bottom: 28, left: 8 }}
              >
                <ChartsAxisHighlight x="band" />
                <BarPlot />
                <LinePlot />
                <MarkPlot />
                <ChartsXAxis axisId="day" />
                <ChartsYAxis axisId="shared" label="kWh/kWp · hours" />
                <ChartsTooltip trigger="axis" />
              </ChartContainer>
            </>
          )}
        </SectionCard>

      </Box>

      {/* ── The operator's "by inverter" page ────────────────────────────── */}
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <SectionCard title="Specific production by inverter (kWh/kWp)" sx={{ minWidth: 0 }}
                     caption="PCUs across, the four inverter slots down — the operator's own layout. Shaded so the LOW cells carry the ink, because an underperformer is what this grid is read for. The dashed cell is PCU 11 inverter 4, a SCADA tag slot with no inverter behind it; their grid leaves it blank too.">
          {loading ? <Skeleton height={120} /> : (
            <PcuGrid rows={fleet} field="specific_production" unit="kWh/kWp"
                     digits={2} colour="#3b82f6" worseWhen="low" />
          )}
        </SectionCard>

        <SectionCard title="Performance ratio by inverter (%)" sx={{ minWidth: 0 }}
                     caption={data?.fleet?.basis}>
          {loading ? <Skeleton height={120} /> : (
            <PcuGrid rows={fleet} field="performance_ratio" unit="%" scale={100}
                     digits={2} colour={GOOD} worseWhen="low" />
          )}
        </SectionCard>

        <SectionCard title="Outage hours by inverter" sx={{ minWidth: 0 }}
                     caption="Ink follows the HIGH cells here — the opposite of the two grids above, because for downtime it is the large numbers that matter. Totals per PCU along the bottom.">
          {loading ? <Skeleton height={140} /> : (
            <PcuGrid rows={fleet} field="outage_hours" unit="h" digits={2}
                     colour={BAD} worseWhen="high" showTotals />
          )}
        </SectionCard>

        <SectionCard title="Failure mode" sx={{ minWidth: 0 }}
          caption="Yield against downtime — separates “it was off” from “it ran and still underperformed”. Bottom-right is an availability fault; bottom-left ran all period and is still short, so soiling, string mismatch or degradation — a different fault and a different truck roll.">
          {loading ? <Skeleton height={300} /> : (
            <FailureModeScatter rows={fleet} mean={stats.mean} />
          )}
        </SectionCard>

        {excluded.length > 0 && (
          <Alert severity="info">
            Excluded from the fleet: {excluded.map((e) => e.display_name).join(', ')} —
            a SCADA tag slot with no combiner boxes and no energy. PVsyst specifies
            75 inverters; the export carries 76 tag sets.
          </Alert>
        )}
      </Stack>
    </>
  );
}


// ── strings — deck slides 17-19 ─────────────────────────────────────────────

function StringsPanel({ cap, period, setPeriod, presets }) {
  const { data, loading, error, refresh } = useScadaPanel('combiners', cap, null, period);
  const boxes = useMemo(() => data?.combiner_boxes || [], [data]);
  const worst = boxes.slice(0, 25);

  // Inverters with three or more boxes in the worst decile — a cluster points at
  // the inverter, a lone box points at the box.
  const clusters = useMemo(() => {
    const cut = Math.ceil(boxes.length * 0.1);
    const counts = {};
    boxes.slice(0, cut).forEach((b) => {
      counts[b.inverter] = (counts[b.inverter] || 0) + 1;
    });
    return Object.entries(counts).filter(([, n]) => n >= 3)
      .sort((a, b) => b[1] - a[1]);
  }, [boxes]);
  const clusterSet = useMemo(() => new Set(clusters.map(([inv]) => inv)), [clusters]);

  return (
    <>
      <PanelHeader title="Combiner boxes" cap={cap} onRefresh={refresh}
        period={period} onPeriodChange={setPeriod} presets={presets}
        />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={2}>
        <SectionCard title="DC collection map"
          caption={`all ${boxes.length} combiner boxes · scroll for the full string`}>
          {loading ? <Skeleton height={220} /> : (
            <CollectionGrid boxes={boxes} span={30} />
          )}
        </SectionCard>

        <SectionCard title="Deviation distribution"
          caption="where the tail sits across the whole DC collection">
          {loading ? <Skeleton height={130} /> : (
            <DeviationHistogram values={boxes.map((b) => b.deviation_pct)} span={30} />
          )}
        </SectionCard>

        <SectionCard title="Worst 25 by deviation"
          caption="all 25 are genuinely extreme, so the bars share one hue and the CLUSTER tag marks the boxes that share an inverter">
          {loading ? <Skeleton height={320} /> : (
            <>
              <EmphasisBars threshold={0} rows={worst.map((b) => ({
                label: b.display_name,
                value: b.deviation_pct,
                // Uniform accent: one series, one colour. Magnitude is bar length.
                flagged: true,
                sub: b.inverter ? b.inverter.split('.').slice(-2).join('.') : null,
                badge: clusterSet.has(b.inverter) ? 'CLUSTER' : null,
              }))} />
              <Typography variant="caption" color="text.secondary"
                          sx={{ fontSize: 10, mt: 1.5, display: 'block' }}>
                A CLUSTER tag means other boxes on the same inverter are also in the
                worst decile — one inverter-level fault, one work order. An untagged
                row is an isolated box, so the box or its strings. Note the worst
                individual boxes here are isolated, while the clustered ones sit
                lower in the ranking: two different faults, two different remedies.
              </Typography>
            </>
          )}
        </SectionCard>

        <SectionCard title="Ranked detail">
          {loading ? <Skeleton height={260} /> : (
            <TableContainer sx={{ maxHeight: 420, overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Rank', 'Combiner box', 'Inverter', 'Strings', 'Ah',
                      'Ah/string', 'Deviation'].map((h) =>
                      <TableCell key={h}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boxes.slice(0, 60).map((b) => (
                    <TableRow key={b.parent}>
                      <TableCell>{b.rank}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{b.display_name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                        {b.inverter}
                      </TableCell>
                      <TableCell>{b.strings ?? '—'}</TableCell>
                      <TableCell>{fmtInt(b.ah)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{fmtNum(b.ah_per_string, 1)}</TableCell>
                      <TableCell sx={{ color: devColour(b.deviation_pct, { warn: 5, bad: 15 }),
                                       fontWeight: 600 }}>
                        {fmtNum(b.deviation_pct, 2)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      </Stack>
    </>
  );
}


// ── Shell ───────────────────────────────────────────────────────────────────

const PANELS = {
  performance: PerformancePanel,
  production: ProductionPanel,
  sensors: SensorsPanel,
  availability: AvailabilityPanel,
  inverters: InvertersPanel,
  strings: StringsPanel,
};

export default function ScadaSubview({ panel: panelProp }) {
  const { panel: panelParam, portfolioId } = useParams();
  const panel = panelProp || panelParam || 'performance';
  const cap = useScadaCapability(portfolioId);
  // Held in the shell, not per panel, so the window survives moving between tabs
  // — comparing inverters and combiner boxes over different periods without
  // noticing would be the easiest way to reach a wrong conclusion here.
  const { period, setPeriod, presets } = useScadaPeriod(cap);
  useThemeColors();   // subscribe so panels restyle with the theme toggle

  const Panel = PANELS[panel] || PerformancePanel;

  if (cap.loading) {
    return (
      <Box sx={{ p: 3 }}>
        <TechnicalWorkspaceTabs />
        <Skeleton height={40} width={280} />
        <Skeleton height={220} sx={{ mt: 2 }} />
      </Box>
    );
  }

  // Two independent gates. Naming which one is shut matters: "not granted" is an
  // admin task, "not in this portfolio" means the user is simply on the wrong
  // portfolio and the data exists.
  if (!cap.available) {
    const wrongPortfolio = cap.datasetAvailable && cap.inPortfolio === false;
    return (
      <Box sx={{ p: 3 }}>
        <TechnicalWorkspaceTabs />
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>
            {wrongPortfolio
              ? 'This portfolio does not contain the SCADA site'
              : 'No SCADA data linked to this organisation'}
          </AlertTitle>
          {wrongPortfolio ? (
            <>
              Plant data is available for{' '}
              <strong>{cap.siteAssetIds.join(', ') || 'another asset'}</strong>, which
              is not in this portfolio. Add the asset to the portfolio, or switch to
              one that holds it.
            </>
          ) : (
            <>
              Plant SCADA appears here once a client dataset is granted to the
              organisation. {cap.reason ? `(${cap.reason})` : ''}
            </>
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <TechnicalWorkspaceTabs />
      <Panel cap={cap} period={period} setPeriod={setPeriod} presets={presets} />
    </Box>
  );
}
